import { useEffect, useMemo, useRef, useState, useCallback, forwardRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  ScreenShare,
  PhoneOff,
  MessageCircle,
  StickyNote,
  ListChecks,
  Sparkles,
  Wand2,
  Settings,
  X,
  Plus,
  Send,
  Trash2,
  Pencil,
  Users,
  Circle,
  StopCircle,
  Captions,
  Link as LinkIcon,
  Copy,
  Mail,
  UserPlus,
  UserMinus,
  CheckCircle2,
  AlertCircle,
  Paperclip,
  Smile,
  Download,
  FileText,
  LayoutGrid,
  Maximize,
} from "lucide-react";
import { toast } from "sonner";
import AgoraRTC from "agora-rtc-sdk-ng";
import useAgoraSttDecoder from "@/lib/useAgoraSttDecoder";
import STT_LANGUAGES from "@/lib/sttLanguages";
import { getDevicePrefs, setDevicePrefs, getJoinPrefs, hasJoinPrefs, getVideoEffects, setVideoEffects, hasVideoEffectsPref } from "@/lib/devicePrefs";
import { isEffectsSupported, createEffects, applyEffects, disposeEffects, DEFAULT_VIDEO_EFFECTS } from "@/lib/videoEffects";
import { resolvePresetSrc } from "@/lib/backgroundPresets";
import VideoEffectsPanel from "@/components/VideoEffectsPanel";
import TaskDetailDrawer from "@/components/tasks/TaskDetailDrawer";
import TaskFormDialog from "@/components/tasks/TaskFormDialog";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import BloqLogo from "@/components/BloqLogo";
import ContactAutocomplete from "@/components/ContactAutocomplete";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import ChatText, { DEFAULT_REACTIONS, formatBytes } from "@/lib/chatHelpers";

/**
 * Live Meeting Room — Agora-powered.
 * - Requests backend token (server-issued)
 * - Joins channel, publishes local mic+camera
 * - Subscribes to remote users
 * - Mic/camera/screen-share toggles
 * - Side panels: chat, notes, tasks (synced to backend)
 */

// Window-width listener. Returns true when the viewport is in mobile
// territory (≤640px — Tailwind's `sm` breakpoint). Used to switch the
// live-grid page size (6 tiles instead of 9) and to opt the meeting
// room CSS into square tiles. Re-evaluates on resize/orientation.
function useIsMobileViewport(breakpointPx = 640) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(`(max-width: ${breakpointPx}px)`).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(`(max-width: ${breakpointPx}px)`);
    const onChange = (e) => setIsMobile(!!e.matches);
    // Modern browsers: addEventListener; Safari ≤13: addListener fallback.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [breakpointPx]);
  return isMobile;
}

export default function MeetingRoom() {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [joinInfo, setJoinInfo] = useState(null);
  // 🔧 Phase-A2b — incremented after a successful in-meeting task create
  // so the TasksPanel reloads immediately rather than waiting up to 8s
  // for its periodic poll.
  const [tasksReloadKey, setTasksReloadKey] = useState(0);
  const [joining, setJoining] = useState(true);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState(null);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [sidePanel, setSidePanel] = useState(null); // 'chat' | 'notes' | 'tasks' | 'participants' | 'recall' | 'settings' | 'transcript' | null
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { sidePanelRef.current = sidePanel; }, [sidePanel]);
  const [elapsed, setElapsed] = useState(0);
  const [ended, setEnded] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordSaving, setRecordSaving] = useState(false);

  // In-meeting task editing — opens the SAME drawer used by main /app/tasks
  // so the in-meeting task UI is the real task editor, not a stripped-down
  // simplified version. Selecting a task or saving a freshly-created one
  // routes the user into the full drawer.
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [showNewTaskDialog, setShowNewTaskDialog] = useState(false);

  // Workspace members — passed into TaskFormDialog/TaskDetailDrawer so the
  // in-meeting task assignee dropdown lists workspace members (not just
  // "Unassigned"). Same data the main /app/tasks app loads.
  const [members, setMembers] = useState([]);
  // Workspace labels — drives the label-chip picker on the in-meeting
  // task form. Guests will fail the call (workspace_id required) and
  // see an empty picker, which is the right behavior.
  const [meetingLabels, setMeetingLabels] = useState([]);
  useEffect(() => {
    const wid = joinInfo?.workspace_id;
    if (!wid) return;
    let cancel = false;
    api.get(`/workspaces/${wid}/members`).then(({ data }) => {
      if (!cancel) setMembers(data || []);
    }).catch(() => { /* not authorized as a guest — leave empty */ });
    api.get(`/labels`, { params: { workspace_id: wid } }).then(({ data }) => {
      if (!cancel) setMeetingLabels(data || []);
    }).catch(() => { /* guest — labels stay empty */ });
    return () => { cancel = true; };
  }, [joinInfo?.workspace_id]);

  // Full meeting record — populated alongside the existing status poller
  // so the in-meeting Invite modal has host_name + description without
  // duplicating the network call. Stays null for guests if the meeting
  // detail endpoint refuses them; the Invite UI degrades gracefully.
  const [meetingMeta, setMeetingMeta] = useState(null);

  // Live audio-activity (volume) per remote uid — drives the "speaking now"
  // green ring + the mic on/off pill in the Participants panel.
  const [remoteLevels, setRemoteLevels] = useState({});
  const [remoteMicOn, setRemoteMicOn] = useState({});
  const [remoteCamOn, setRemoteCamOn] = useState({});

  // Track who is currently sharing their screen. Local `sharing` covers
  // ourselves; the `remoteSharerUid` state covers other participants
  // (populated by a screen-share-started control event the publisher fires).
  const [remoteSharerUid, setRemoteSharerUid] = useState(null);
  // v2.89 polish — name of the active remote sharer. Set from
  // `screen_share_started` event params or from the active-screenshare
  // recovery fetch on join (late joiners). Auto-cleared by the useEffect
  // below whenever `remoteSharerUid` goes null, so we never have to
  // remember to clear it manually in every code path that nulls the UID.
  const [remoteSharerOwnerName, setRemoteSharerOwnerName] = useState(null);
  useEffect(() => {
    if (remoteSharerUid == null) setRemoteSharerOwnerName(null);
  }, [remoteSharerUid]);
  // When WE are sharing, our screenClient joins the channel as a SECOND
  // Agora UID. The main client sees that UID as a "remote user", which
  // would otherwise spill our own screen track into the side strip on
  // top of our camera tile. Track that UID so we can filter it out.
  const [localScreenUid, setLocalScreenUid] = useState(null);
  const someoneIsSharing = sharing || remoteSharerUid != null;

  // ── Live meeting layout selector ─────────────────────────────────────
  // Declared HERE (high in the component) so the camera-replay useEffect
  // below can list these in its dependency array without hitting the
  // Temporal Dead Zone. State is persisted per-browser via localStorage.
  // Strictly affects LIVE UI; never mutates recording layout state.
  //
  // Page size is window-width-aware: 6 tiles per page on mobile (≤640px)
  // so each square tile stays comfortably large; 9 on tablet/desktop
  // where there's room for a 3x3 grid. The `useIsMobile` listener swaps
  // the value on viewport resize.
  const isMobile = useIsMobileViewport();
  const TILES_PER_PAGE = isMobile ? 6 : 9;
  const [tilePage, setTilePage] = useState(0);

  // Mark <body> as "in a meeting" so global widgets (Charla chat) can opt
  // out via CSS. Class removed on unmount so the widget reappears when
  // the user leaves the room. This is the ONLY hook touching <body> —
  // it's cheap and doesn't risk unmount-order races.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.add("in-meeting-room");
    return () => {
      document.body.classList.remove("in-meeting-room");
    };
  }, []);
  const [layoutMode, setLayoutMode] = useState(() => {
    try {
      const v = localStorage.getItem("bloq_meeting_layout");
      // Sidebar was removed (Message 5xxx) — migrate any stale value to Auto.
      if (v === "sidebar") return "auto";
      return v || "auto";
    } catch { return "auto"; }
  });
  const [tileDensity, setTileDensity] = useState(() => {
    try { return localStorage.getItem("bloq_meeting_density") || "comfortable"; } catch { return "comfortable"; }
  });
  const [videoFraming, setVideoFraming] = useState(() => {
    try { return localStorage.getItem("bloq_meeting_framing") || "fill"; } catch { return "fill"; }
  });
  const [spotlightUid, setSpotlightUid] = useState(null);
  const [layoutMenuOpen, setLayoutMenuOpen] = useState(false);
  useEffect(() => { try { localStorage.setItem("bloq_meeting_layout", layoutMode); } catch { /* storage disabled */ } }, [layoutMode]);
  useEffect(() => { try { localStorage.setItem("bloq_meeting_density", tileDensity); } catch { /* storage disabled */ } }, [tileDensity]);
  useEffect(() => { try { localStorage.setItem("bloq_meeting_framing", videoFraming); } catch { /* storage disabled */ } }, [videoFraming]);
  useEffect(() => { if (someoneIsSharing) setLayoutMenuOpen(false); }, [someoneIsSharing]);

  // Ref mirror of sidePanel so the chat poller closure reads the latest value.
  const sidePanelRef = useRef(null);

  // Chat unread badge — chat poll runs centrally here even when the panel
  // isn't open so the toolbar badge updates accurately.
  const [chatTotal, setChatTotal] = useState(0);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  useEffect(() => {
    const mid = joinInfo?.meeting_id;
    if (!mid) return;
    let cancel = false;
    const seenKey = `bloq_chat_seen_count:${mid}`;
    const poll = async () => {
      try {
        const { data } = await api.get(`/meetings/${mid}/chat`);
        if (cancel) return;
        const list = Array.isArray(data) ? data : [];
        setChatMessages(list);
        setChatTotal(list.length);
        // Count messages NOT authored by me that the user hasn't seen yet.
        let seen = 0;
        try { seen = parseInt(window.sessionStorage.getItem(seenKey) || "0", 10); } catch {}
        const newOnes = list.slice(seen);
        const fromOthers = newOnes.filter((m) => {
          if (user?.user_id) return m.author_user_id !== user.user_id;
          // For guests, dedupe by the local guest_session_id (set by /agora/join)
          const sid = (() => { try { return localStorage.getItem("bloq_guest_session_id"); } catch { return null; }})();
          return !sid || m.author_guest_session_id !== sid;
        });
        // If the chat panel is open, treat as already-seen.
        if (sidePanelRef.current === "chat") {
          try { window.sessionStorage.setItem(seenKey, String(list.length)); } catch {}
          setUnreadChatCount(0);
        } else {
          setUnreadChatCount(fromOthers.length);
        }
      } catch { /* non-blocking */ }
    };
    poll();
    const id = setInterval(poll, 4000);
    return () => { cancel = true; clearInterval(id); };
    // eslint-disable-next-line
  }, [joinInfo?.meeting_id, user?.user_id]);

  // Waiting room count for the Participants tab badge — only host polls.
  const [waitingCount, setWaitingCount] = useState(0);
  useEffect(() => {
    const mid = joinInfo?.meeting_id;
    // v2.90 — both host and co-hosts now poll the waiting-room count so
    // their badge can show without opening the panel. Backend permits
    // both via `require_meeting_access` + downstream admit/deny checks.
    if (!mid || !(joinInfo?.is_host || joinInfo?.is_cohost)) return;
    let cancel = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/waiting-room/meeting/${mid}`);
        if (cancel) return;
        setWaitingCount((data || []).filter((w) => w.status === "waiting").length);
      } catch { /* non-blocking */ }
    };
    poll();
    const id = setInterval(poll, 4000);
    return () => { cancel = true; clearInterval(id); };
  }, [joinInfo?.meeting_id, joinInfo?.is_host, joinInfo?.is_cohost]);

  // Live map of agora uid → participant record so RemoteTile labels can
  // show real names (e.g. "Alex") instead of the generic "Participant".
  // Polls the participants endpoint every 4s — same cadence as the
  // ParticipantsPanel — so any rename or rejoin is picked up quickly.
  const [participantsByUid, setParticipantsByUid] = useState({});
  useEffect(() => {
    const mid = joinInfo?.meeting_id;
    if (!mid) return;
    let cancel = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/meetings/${mid}/participants`);
        if (cancel) return;
        const next = {};
        for (const p of data || []) {
          if (p?.uid != null) next[p.uid] = p;
        }
        setParticipantsByUid(next);
      } catch { /* non-blocking */ }
    };
    poll();
    const t = setInterval(poll, 4000);
    return () => { cancel = true; clearInterval(t); };
  }, [joinInfo?.meeting_id]);

  // v2.93b — Live host/co-host flags. `joinInfo` is captured ONCE at
  // join time, so a user promoted to co-host AFTER joining still has
  // `joinInfo.is_cohost === false`. Derive the role from the participants
  // poll (refreshed every 4s) so the toolbar buttons (Recording, End
  // for all) and side-panel gates update as soon as a promotion lands.
  // Boot-time `joinInfo` values are used as fallbacks for the first
  // ~4s before the first poll completes.
  const myParticipantRow = joinInfo?.uid != null ? participantsByUid[joinInfo.uid] : null;
  const liveIsHost = myParticipantRow ? !!myParticipantRow.is_host : !!joinInfo?.is_host;
  const liveIsCohost = myParticipantRow ? !!myParticipantRow.is_cohost : !!joinInfo?.is_cohost;
  const canModerateMeeting = !!(liveIsHost || liveIsCohost);

  // NOTE: Browser SpeechRecognition / Web Speech is INTENTIONALLY NOT
  // wired into the meeting room. It only captures the local user's
  // microphone, which produces misleading "full meeting" transcripts
  // that AI Summary would then read. Full-meeting transcription via
  // Agora Real-Time STT is the only supported provider. If Agora STT
  // is not configured the panel says so — it never silently falls
  // back to the host's mic.

  // ---- Agora Real-Time Speech-to-Text (full-meeting transcription) -----
  // Capability check the meeting room polls once on join so the panel can
  // expose the "Use Full Meeting Transcription" button. The browser-only
  // Web Speech path above is always available as a fallback regardless.
  const [sttProvider, setSttProvider] = useState({ provider: null, configured: false });
  // Workspace-level transcription defaults — language picker preselects
  // this, and the auto-start flag triggers a one-shot start on join.
  const [sttWorkspaceDefaults, setSttWorkspaceDefaults] = useState({
    default_transcription_language: "en-US",
    transcription_auto_start: false,
  });
  // Session metadata returned by /transcription/start (or hydrated from
  // /status on rejoin). Drives the decoder + the live transcript poller.
  const [sttSession, setSttSession] = useState(null); // { transcript_id, agent_id, status, bot_uid } | null
  // Full-meeting transcript entries — populated by polling once we have
  // a live session. This is the ONLY transcript buffer the UI shows;
  // browser SpeechRecognition is no longer wired in.
  const [sttEntries, setSttEntries] = useState([]);
  const sttCursorRef = useRef(null);
  useEffect(() => {
    let cancel = false;
    api.get("/transcription/provider").then(({ data }) => {
      if (!cancel) setSttProvider(data || { provider: null, configured: false });
    }).catch(() => { /* leave default — frontend falls back to webspeech */ });
    return () => { cancel = true; };
  }, []);
  // Hydrate any pre-existing session when we join, so transcription
  // continues seamlessly across rejoins.
  useEffect(() => {
    const mid = joinInfo?.meeting_id;
    if (!mid) return;
    api.get(`/meetings/${mid}/transcription/status`).then(({ data }) => {
      if (data?.mode === "agora_full_meeting" && data.status === "live") {
        setSttSession({
          transcript_id: data.transcript_id,
          agent_id: null,
          status: "live",
          bot_uid: data.bot_uid,
        });
      }
    }).catch(() => {});
  }, [joinInfo?.meeting_id]);
  // Fetch workspace transcription defaults so the panel's language
  // picker preselects the workspace's default and we know whether to
  // auto-start on join.
  useEffect(() => {
    const wid = joinInfo?.workspace_id;
    if (!wid) return;
    let cancel = false;
    api.get(`/workspaces/${wid}`).then(({ data }) => {
      if (cancel || !data) return;
      setSttWorkspaceDefaults({
        default_transcription_language: data.default_transcription_language || "en-US",
        transcription_auto_start: data.transcription_auto_start === true,
      });
    }).catch(() => { /* guest may not have workspace read; leave defaults */ });
    return () => { cancel = true; };
  }, [joinInfo?.workspace_id]);
  // Decoder pipeline is wired AFTER clientRef declaration below to avoid
  // a forward reference. See `useAgoraSttDecoder` call further down.

  // React-state mirror of clientRef so hooks (like the STT decoder) that
  // need to re-run when the Agora client is (re-)created can subscribe.
  const [agoraClient, setAgoraClient] = useState(null);
  // Live transcript poll — pulls newly-ingested entries from the backend
  // every 3s while a session is active. Polls keep going even when the
  // user is on another side panel (lifecycle is meeting-level).
  useEffect(() => {
    const mid = joinInfo?.meeting_id;
    if (!mid || !sttSession || sttSession.status !== "live") return;
    let cancel = false;
    const tick = async () => {
      try {
        const params = sttCursorRef.current ? { after: sttCursorRef.current } : {};
        const { data } = await api.get(`/meetings/${mid}/transcription/transcript`, { params });
        if (cancel) return;
        const newEntries = (data?.entries || []);
        if (newEntries.length) {
          setSttEntries((arr) => [...arr, ...newEntries]);
          sttCursorRef.current = newEntries[newEntries.length - 1].created_at;
        }
      } catch { /* silent — transient errors */ }
    };
    tick();
    const t = setInterval(tick, 3000);
    return () => { cancel = true; clearInterval(t); };
  }, [joinInfo?.meeting_id, sttSession]);

  const startAgoraStt = useCallback(async (languageOverride) => {
    const mid = joinInfo?.meeting_id;
    if (!mid) return;
    try {
      const { data } = await api.post(`/meetings/${mid}/transcription/start`, {
        // Caller may pass a single BCP-47 code (from the panel dropdown)
        // or omit to let the backend fall back to the workspace default.
        languages: languageOverride ? [languageOverride] : undefined,
      });
      setSttSession({
        transcript_id: data.transcript_id,
        agent_id: data.agent_id,
        status: data.status || "live",
        bot_uid: data.bot_uid,
      });
      sttCursorRef.current = null;
      setSttEntries([]);
      toast.success("Full-meeting transcription started.");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Could not start";
      if (detail === "provider_not_configured") {
        toast.error("Agora STT is not configured. Use the browser-local fallback below.");
      } else {
        toast.error(`Could not start full-meeting transcription: ${detail}`);
      }
    }
  }, [joinInfo?.meeting_id]);

  const stopAgoraStt = useCallback(async () => {
    const mid = joinInfo?.meeting_id;
    if (!mid) return;
    try {
      await api.post(`/meetings/${mid}/transcription/stop`);
      setSttSession((s) => (s ? { ...s, status: "completed" } : s));
      toast.success("Full-meeting transcription stopped.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not stop";
      toast.error(`Could not stop transcription: ${detail}`);
    }
  }, [joinInfo?.meeting_id]);

  // Auto-start full-meeting transcription on join when:
  //   1. Workspace has `transcription_auto_start = true`
  //   2. Agora STT provider is configured
  //   3. Current user is the meeting host (we don't want every workspace
  //      member who joins to race to start it; host is the safe gate)
  //   4. No session is already live (avoid double-start races)
  // Only fires once per (meeting, mount) — guarded by a ref so a
  // workspace-default refetch doesn't retrigger it.
  const autoStartFiredRef = useRef(false);
  useEffect(() => {
    if (autoStartFiredRef.current) return;
    if (!joinInfo?.is_host) return;
    if (!sttProvider?.configured) return;
    if (!sttWorkspaceDefaults?.transcription_auto_start) return;
    if (sttSession && (sttSession.status === "live" || sttSession.status === "starting")) return;
    autoStartFiredRef.current = true;
    // 3-second delay so the meeting is fully joined to Agora AND the
    // backend has registered the host's `agora_channel_name` and
    // `meeting_participants.uid`. STT will refuse to start if there are
    // no published participants yet (409), so we want to be on the
    // safe side. The user can also start manually from the panel at
    // any time.
    const t = setTimeout(() => {
      startAgoraStt(sttWorkspaceDefaults.default_transcription_language || "en-US");
    }, 3000);
    return () => clearTimeout(t);
  }, [
    joinInfo?.is_host,
    sttProvider?.configured,
    sttWorkspaceDefaults?.transcription_auto_start,
    sttWorkspaceDefaults?.default_transcription_language,
    sttSession,
    startAgoraStt,
  ]);

  // ----------------------------------------------------------------------

  const clientRef = useRef(null);
  // Dedicated 2nd Agora client used ONLY to publish the screen-share track
  // from a derived UID — keeps the main client's camera publish intact so
  // every participant continues to see the host's camera in the side rail
  // even while the screen is being shared. (Agora NG single-client limits
  // a UID to one video track at a time.)
  const screenClientRef = useRef(null);
  const localTracksRef = useRef({ audio: null, camera: null, screen: null, screenAudio: null });
  const localVideoRef = useRef(null);
  // Dedicated DOM ref for the local user's screen-share track when WE are
  // the one sharing. Kept SEPARATE from `localVideoRef` so the local camera
  // tile and the local screen-share tile render independently — fixes the
  // bug where the screen track replaced the camera feed in the side strip.
  const localScreenVideoRef = useRef(null);
  const remoteRefs = useRef({});
  const joinStart = useRef(null);
  const participantIdRef = useRef(null);
  const prefsRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordChunksRef = useRef([]);

  // Lobby-first guard: regardless of whether the user is logged in, every
  // attempt to enter a meeting MUST go through the Lobby so they can adjust
  // their camera, mic, and display name. If we land here without having
  // completed the Lobby for this specific public_id, we redirect back.
  useEffect(() => {
    if (!publicId) return;
    if (!hasJoinPrefs(publicId)) {
      navigate(`/m/${publicId}`, { replace: true });
    }
    // eslint-disable-next-line
  }, [publicId]);

  // 1. Fetch token from backend. The backend may respond with a "waiting
  // room" payload instead of a token — in that case we don't try to join
  // Agora and instead show the WaitingRoomScreen below. We poll the same
  // endpoint every ~3 seconds; when the host admits us, the backend will
  // start returning a real token and the page transitions into the room.
  const [waitingRoom, setWaitingRoom] = useState(null); // null | { meeting_title, host_name, display_name }
  useEffect(() => {
    if (!publicId) return;
    // Honor the lobby-first guard above; if we have no prefs for this
    // meeting we're about to be redirected, so don't request a token.
    if (!hasJoinPrefs(publicId)) return;
    let cancel = false;
    let pollTimer = null;
    const requestToken = async () => {
      try {
        const joinPrefs = getJoinPrefs(publicId);
        const devicePrefs = getDevicePrefs();
        const prefs = { ...joinPrefs, ...devicePrefs };
        prefsRef.current = prefs;
        // Stable per-browser identity so polling /agora/join while waiting
        // never duplicates the participant or the waiting-room row.
        let guestSessionId = localStorage.getItem("bloq_guest_session_id");
        if (!guestSessionId) {
          guestSessionId = `gs_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
          localStorage.setItem("bloq_guest_session_id", guestSessionId);
        }
        const { data } = await api.post("/agora/join", {
          meeting_public_id: publicId,
          display_name: prefs.displayName || user?.name || "Guest",
          guest_session_id: guestSessionId,
        });
        if (cancel) return;
        if (data?.waiting_room) {
          setWaitingRoom({
            meeting_title: data.meeting_title,
            host_name: data.host_name,
            display_name: data.display_name,
          });
          // Keep polling — the moment the host admits us, the next response
          // will be a full token and we slide into the meeting.
          pollTimer = setTimeout(requestToken, 3000);
          return;
        }
        // Admitted (or never had a waiting room). Hide the waiting screen
        // and continue to Agora setup.
        setWaitingRoom(null);
        setJoinInfo(data);
        participantIdRef.current = data.participant_id;
      } catch (err) {
        if (cancel) return;
        const status = err.response?.status;
        const detail = err.response?.data?.detail || "Could not get a meeting token";
        // 410 = ended / canceled → unavailable page (no live room available)
        if (status === 410) {
          navigate(`/m/${publicId}/unavailable`, { replace: true });
          return;
        }
        // 403 with "denied" message means the host rejected — bail out.
        if (status === 403 && /denied/i.test(detail)) {
          setError(detail);
          setJoining(false);
          return;
        }
        setError(detail);
        setJoining(false);
      }
    };
    requestToken();
    return () => { cancel = true; if (pollTimer) clearTimeout(pollTimer); };
  }, [publicId, user]);

  const [deviceError, setDeviceError] = useState(null);

  // ── Video effects (background blur/image + beauty) — per-participant,
  //    client-side only. Handles are bound to the current camera track and
  //    re-created whenever the camera track is re-acquired (toggleCam).
  const effectsRef = useRef(null);
  const bgImageElRef = useRef(null);
  const [effectsSupported] = useState(() => isEffectsSupported());
  const [videoEffects, setVideoEffectsState] = useState(() => getVideoEffects());
  const [effectsOpen, setEffectsOpen] = useState(false);

  // 2. Join Agora once we have the token
  const joinChannel = useCallback(async () => {
    if (!joinInfo) return;
    try {
      const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
      clientRef.current = client;
      setAgoraClient(client);

      client.on("user-published", async (rUser, mediaType) => {
        await client.subscribe(rUser, mediaType);
        // Preserve prior track refs across media-type events. Agora fires
        // `user-published` once per media-type. If we naively wrote both
        // `videoTrack: rUser.videoTrack` and `audioTrack: rUser.audioTrack`
        // on every event, a fast audio→video sequence could wipe the
        // freshly-subscribed track due to a closure race on the await.
        setRemoteUsers((prev) => {
          const existing = prev.find((u) => u.uid === rUser.uid);
          const without = prev.filter((u) => u.uid !== rUser.uid);
          return [
            ...without,
            {
              uid: rUser.uid,
              videoTrack:
                mediaType === "video"
                  ? rUser.videoTrack || existing?.videoTrack || null
                  : existing?.videoTrack || rUser.videoTrack || null,
              audioTrack:
                mediaType === "audio"
                  ? rUser.audioTrack || existing?.audioTrack || null
                  : existing?.audioTrack || rUser.audioTrack || null,
            },
          ];
        });
        if (mediaType === "audio") {
          rUser.audioTrack?.play();
          setRemoteMicOn((m) => ({ ...m, [rUser.uid]: true }));
        }
        if (mediaType === "video") {
          setRemoteCamOn((c) => ({ ...c, [rUser.uid]: true }));
        }
      });
      client.on("user-unpublished", (rUser, mediaType) => {
        if (mediaType === "video") {
          setRemoteUsers((prev) =>
            prev.map((u) => (u.uid === rUser.uid ? { ...u, videoTrack: null } : u))
          );
          setRemoteCamOn((c) => ({ ...c, [rUser.uid]: false }));
        }
        if (mediaType === "audio") {
          setRemoteMicOn((m) => ({ ...m, [rUser.uid]: false }));
        }
      });
      client.on("user-left", (rUser) => {
        // v2.89 — if the user who just left was the active screen-sharer,
        // clear `remoteSharerUid` so the sharing-layout primary tile
        // doesn't stay stuck on an absent UID (which would render as a
        // black box because `remoteUsers.find(...)` returns undefined
        // for the now-departed screen UID). The functional updater means
        // we only mutate when the leaving UID actually matches the
        // active sharer — no false clears for unrelated user-left
        // events. The sharer's main camera UID is a DIFFERENT UID than
        // their screen UID, so both fire user-left when the sharer
        // closes the tab; only one of them will match remoteSharerUid.
        setRemoteSharerUid((cur) => (cur != null && Number(cur) === Number(rUser.uid)) ? null : cur);
        setRemoteUsers((prev) => prev.filter((u) => u.uid !== rUser.uid));
        setRemoteLevels((lv) => { const n = { ...lv }; delete n[rUser.uid]; return n; });
        setRemoteMicOn((m) => { const n = { ...m }; delete n[rUser.uid]; return n; });
        setRemoteCamOn((c) => { const n = { ...c }; delete n[rUser.uid]; return n; });
      });
      // Per-uid live volume indicator. Agora fires this every ~2s with the
      // current input level (0..100) for each publisher in the channel.
      try {
        client.enableAudioVolumeIndicator();
        client.on("volume-indicator", (volumes) => {
          setRemoteLevels((prev) => {
            const next = { ...prev };
            for (const v of volumes || []) {
              next[v.uid] = v.level || 0;
            }
            return next;
          });
        });
      } catch { /* SDK may not have audio-volume on older builds — non-fatal */ }

      await client.join(joinInfo.app_id, joinInfo.channel_name, joinInfo.token, joinInfo.uid);

      const prefs = prefsRef.current || {};
      // Create local mic + camera tracks honouring the lobby's saved
      // device selections. We create each track independently so the user
      // can join even when:
      //   - only mic permission is granted (no camera plugged in)
      //   - only camera permission is granted (no mic plugged in)
      //   - the user wants to enter with BOTH mic AND camera off — in that
      //     case we skip device requests entirely and join the channel as
      //     a "view-only" participant. They can re-enable either device
      //     later through the meeting controls.
      // Previously this code went through createMicrophoneAndCameraTracks()
      // which throws if EITHER device fails — that was the "cannot join"
      // bug when the user turned both off.
      let audioTrack = null;
      let videoTrack = null;
      const wantMic = prefs.micOn !== false;
      const wantCam = prefs.camOn !== false;
      const tryMic = async () => {
        const opts = prefs.micId ? { microphoneId: prefs.micId } : {};
        try { return await AgoraRTC.createMicrophoneAudioTrack(opts); }
        catch (e1) {
          try { return await AgoraRTC.createMicrophoneAudioTrack({}); }
          catch (e2) { console.warn("Mic create failed", e2); return null; }
        }
      };
      const tryCam = async () => {
        const opts = prefs.camId ? { cameraId: prefs.camId } : {};
        try { return await AgoraRTC.createCameraVideoTrack(opts); }
        catch (e1) {
          try { return await AgoraRTC.createCameraVideoTrack({}); }
          catch (e2) { console.warn("Camera create failed", e2); return null; }
        }
      };
      if (wantMic || !wantCam) {
        // If the user wants the mic on, try to acquire it. We also try when
        // both are off — the only case we skip entirely is when *only* the
        // camera is wanted (see below).
        if (wantMic) audioTrack = await tryMic();
      }
      if (wantCam) {
        videoTrack = await tryCam();
      }
      if (!audioTrack && !videoTrack) {
        // Both off (intentionally) or both unavailable. Continue without
        // publishing any local tracks — the user enters as a present
        // participant with mic & cam off. They can turn either on later.
        setMicOn(false);
        setCamOn(false);
      }
      localTracksRef.current.audio = audioTrack;
      localTracksRef.current.camera = videoTrack;

      // Honor lobby toggles for whatever tracks were successfully created.
      if (audioTrack) {
        if (prefs.micOn === false) {
          await audioTrack.setEnabled(false);
          setMicOn(false);
        }
      } else { setMicOn(false); }
      if (videoTrack) {
        if (prefs.camOn === false) {
          await videoTrack.setEnabled(false);
          setCamOn(false);
        }
      } else { setCamOn(false); }

      if (videoTrack && localVideoRef.current) videoTrack.play(localVideoRef.current);

      const toPublish = [audioTrack, videoTrack].filter(Boolean);
      if (toPublish.length) {
        await client.publish(toPublish);
      }

      joinStart.current = Date.now();
      setJoined(true);
      setJoining(false);
    } catch (err) {
      console.error(err);
      setError("Could not join the meeting room — please reload and try again.");
      setJoining(false);
    }
  }, [joinInfo]);

  // Ensure the local camera track is attached to the LocalTile ref AFTER the
  // tile mounts. Without this, the very first `videoTrack.play()` call inside
  // joinChannel runs before <LocalTile> renders (joined=false until the very
  // last line of joinChannel) — so the local video container exists only after
  // joining, but the play() call has already happened against a null ref.
  // Re-attaching here is idempotent and covers ref remounts (e.g. when the
  // user opens/closes a side panel that re-layouts the grid).
  //
  // 🔧 v2.88 — added `remoteSharerUid` + `localScreenUid` to the deps. The
  // layout ternary at the JSX render flips on `someoneIsSharing = sharing ||
  // remoteSharerUid != null`. When a REMOTE participant starts/stops sharing
  // we are NOT the sharer, so `sharing` stays false — but the layout still
  // swaps, which unmounts and remounts <LocalTile> into a different parent
  // (the sharing strip ↔ the non-sharing grid). The shared `localVideoRef`
  // gets pointed at a fresh DOM container during the commit, but the Agora
  // <video> element was attached to the OLD container (now removed by
  // React). Without `remoteSharerUid` in this dep array, NO code path
  // re-bound the camera and the self-view tile went black until the user
  // clicked Settings (which toggles `sidePanel` and re-fires this effect).
  useEffect(() => {
    if (!joined) return;
    // Camera ALWAYS goes to the camera tile ref. Screen NEVER goes here.
    const cam = localTracksRef.current.camera;
    if (cam && localVideoRef.current) {
      try { cam.play(localVideoRef.current); } catch { /* idempotent re-attach */ }
    }
  }, [joined, sharing, remoteSharerUid, localScreenUid, sidePanel, layoutMode, tilePage, tileDensity]);

  // Keep the local screen-share track attached to the dedicated screen ref
  // so the primary tile shows the screen, never the camera. Runs whenever
  // the layout flips (sharing/sidePanel) so a re-mount of the screen tile
  // immediately picks up the existing screen track.
  useEffect(() => {
    if (!joined || !sharing) return;
    const scr = localTracksRef.current.screen;
    if (scr && localScreenVideoRef.current) {
      try { scr.play(localScreenVideoRef.current); } catch {}
    }
  }, [joined, sharing, sidePanel]);

  useEffect(() => {
    if (joinInfo && !joined) joinChannel();
  }, [joinInfo, joined, joinChannel]);

  // Browser-only auto-start was removed. Full-meeting auto-start is
  // handled by the Agora STT auto-start effect above, gated on workspace
  // setting + host role + provider configured.

  // v2.89 — Active-screenshare state recovery for late joiners.
  // Fires once on `joined === true`. Asks the backend whether anyone
  // is currently screen-sharing (derived from the latest
  // `screen_share_started` / `screen_share_stopped` event in
  // `db.meeting_control_events`). If yes, sets `remoteSharerUid` so:
  //   1. `liveRemoteUsers` filters the screen UID out of the camera
  //      grid (line ~1193).
  //   2. `someoneIsSharing` flips true → layout swaps to sharing mode.
  //   3. The primary tile resolves to the screen track that Agora
  //      already auto-subscribed for us via user-published.
  // Without this, late joiners see the screen track rendered as a
  // regular camera tile because `screen_share_started` was emitted
  // before they began polling.
  //
  // We use a functional updater so we never override an in-progress
  // local share (we'd have `localScreenUid` set + `sharing===true`).
  useEffect(() => {
    if (!joined || !joinInfo?.meeting_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/meetings/${joinInfo.meeting_id}/active-screenshare`);
        if (cancelled) return;
        if (data?.active && data?.uid != null) {
          setRemoteSharerUid((cur) => cur != null ? cur : Number(data.uid));
          if (data.owner_name) setRemoteSharerOwnerName(data.owner_name);
        }
      } catch {/* fire-and-forget — next control-event poll will catch any future change */}
    })();
    return () => { cancelled = true; };
  }, [joined, joinInfo?.meeting_id]);

  // Timer
  useEffect(() => {
    if (!joined) return;
    const t = setInterval(() => {
      if (joinStart.current) setElapsed(Math.floor((Date.now() - joinStart.current) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [joined]);

  // Poll moderator control events (mute / remove / end-for-all / camera /
  // unmute-request signals). Hosts emit, every other client polls every 4s
  // and reacts. End-for-all is also a hard backend stop — meeting.status
  // becomes "ended" — but the signal makes the redirect instant instead of
  // waiting for the next status poll.
  const [unmuteRequest, setUnmuteRequest] = useState(null); // {by_name, type:'mic'|'cam'}
  useEffect(() => {
    if (!joined || !joinInfo?.meeting_id) return;
    let since = new Date().toISOString();
    const poll = async () => {
      try {
        const { data } = await api.get(`/meetings/${joinInfo.meeting_id}/control-events`, { params: { since } });
        if (data?.length) {
          since = data[data.length - 1].created_at;
          for (const ev of data) {
            const isForMe = !ev.target_participant_id ||
              ev.target_participant_id === participantIdRef.current;
            const isFromMe = ev.by_user_id === user?.user_id;
            if (ev.type === "end_meeting") {
              // Host ended the meeting for all — everyone leaves. Logged-in
              // users go to the dashboard; guests go to the account-CTA
              // page. We redirect IMMEDIATELY and let the Agora cleanup
              // run in the background so the participant is out within
              // ~milliseconds instead of waiting for client.leave() to
              // resolve (which can take 1-3s on a flaky network).
              if (!isFromMe) {
                toast.info("The host ended this meeting");
                setSharing(false);
                setRemoteSharerUid(null);
                (async () => { try { await leave(); } catch {} })();
                if (user?.user_id) {
                  navigate("/app", { replace: true });
                } else {
                  navigate(`/m/${publicId}/thanks`, { replace: true });
                }
              }
              continue;
            }
            // Below events are targeted (or broadcast). Skip if not for me.
            const isBroadcast = !ev.target_participant_id;
            if (!isForMe && !isBroadcast) continue;
            if (isFromMe) continue;
            if (ev.type === "mute") {
              const track = localTracksRef.current.audio;
              if (track && track.enabled) {
                await track.setEnabled(false);
                setMicOn(false);
                toast.info(`Host muted your microphone`);
              }
            } else if (ev.type === "cam_off") {
              const track = localTracksRef.current.camera;
              if (track && track.enabled) {
                await track.setEnabled(false);
                setCamOn(false);
                toast.info(`Host turned off your camera`);
              }
            } else if (ev.type === "mute_all") {
              const track = localTracksRef.current.audio;
              if (track && track.enabled) {
                await track.setEnabled(false);
                setMicOn(false);
                toast.info(`Host muted everyone`);
              }
            } else if (ev.type === "cam_off_all") {
              const track = localTracksRef.current.camera;
              if (track && track.enabled) {
                await track.setEnabled(false);
                setCamOn(false);
                toast.info(`Host turned off all cameras`);
              }
            } else if (ev.type === "unmute_request") {
              setUnmuteRequest({ by_name: ev.by_name || "Host", target: "mic" });
            } else if (ev.type === "cam_on_request") {
              setUnmuteRequest({ by_name: ev.by_name || "Host", target: "cam" });
            } else if (ev.type === "screen_share_started") {
              // Some other participant just started sharing. Their Agora
              // uid is in params.uid so we can promote their tile to
              // primary in the screen-share layout. SKIP for the sharer
              // themselves — they already manage their own state via
              // `sharing` + `localScreenUid`; setting remoteSharerUid for
              // the sharer pollutes state and the value never gets cleared
              // on stop (the stop handler below ignores `isFromMe`).
              if (!isFromMe) {
                setRemoteSharerUid(ev.params?.uid ?? ev.by_user_id ?? null);
                setRemoteSharerOwnerName(ev.params?.owner_name || ev.by_name || null);
              }
            } else if (ev.type === "screen_share_stopped") {
              // ALWAYS clear remoteSharerUid (whether the stop event came
              // from us or another participant). Defensive — the sharer's
              // own toggleShare-stop branch also clears it, but a missed
              // event would otherwise strand them in screen-share layout.
              setRemoteSharerUid(null);
            } else if (ev.type === "remove") {
              toast.error(`You were removed from the meeting by the host`);
              setTimeout(() => leave(), 600);
            }
          }
        }
      } catch {
        // silent
      }
    };
    const id = setInterval(poll, 2000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [joined, joinInfo?.meeting_id]);

  // Backend fallback for end-of-meeting detection. If for any reason a
  // host's `end_meeting` control_event was missed (e.g. network blip
  // during the 4s polling window), we still notice the next time we look
  // at the meeting record — `status === "ended"` means goodbye.
  useEffect(() => {
    if (!joined || !joinInfo?.meeting_id) return;
    let cancel = false;
    const checkEnded = async () => {
      try {
        const { data } = await api.get(`/meetings/${joinInfo.meeting_id}`);
        if (cancel) return;
        // Cache the latest meeting record so the in-meeting Invite modal
        // can render host_name + description without an extra request.
        setMeetingMeta(data || null);
        if (data?.status === "ended" || data?.status === "completed" || data?.status === "canceled") {
          toast.info("The host ended this meeting");
          setSharing(false);
          setRemoteSharerUid(null);
          (async () => { try { await leave(); } catch {} })();
          if (user?.user_id) {
            navigate("/app", { replace: true });
          } else {
            navigate(`/m/${publicId}/thanks`, { replace: true });
          }
        }
      } catch { /* silent */ }
    };
    const id = setInterval(checkEnded, 3000);
    return () => { cancel = true; clearInterval(id); };
    // eslint-disable-next-line
  }, [joined, joinInfo?.meeting_id]);

  const leave = useCallback(async () => {
    try {
      try { if (effectsRef.current) { await disposeEffects(effectsRef.current); effectsRef.current = null; } } catch {}
      const t = localTracksRef.current;
      // Stop and close every local track — including the screen-share
      // track + its (optional) system-audio track — so the "you are
      // sharing" UI never persists into the dashboard or CTA page.
      [t.audio, t.camera, t.screen, t.screenAudio].forEach((tr) => { try { tr?.stop(); tr?.close(); } catch {} });
      localTracksRef.current = { audio: null, camera: null, screen: null, screenAudio: null };
      // Clear screen-share UI state so any "you are sharing" pill / bar
      // disappears the instant the user leaves or the meeting ends.
      setSharing(false);
      setRemoteSharerUid(null);
      setLocalScreenUid(null);
      try { await screenClientRef.current?.leave(); } catch {}
      screenClientRef.current = null;
      try { await clientRef.current?.leave(); } catch {}
      if (participantIdRef.current) {
        await api.post("/agora/leave", { participant_id: participantIdRef.current }).catch(() => {});
      }
    } finally {
      setEnded(true);
    }
  }, []);

  // Auto-cleanup on unmount
  useEffect(() => {
    return () => {
      try { if (effectsRef.current) disposeEffects(effectsRef.current); } catch {}
      const t = localTracksRef.current;
      [t.audio, t.camera, t.screen].forEach((tr) => { try { tr?.stop(); tr?.close(); } catch {} });
      try { clientRef.current?.leave(); } catch {}
    };
  }, []);

  // Build an <img> element for a custom background from a data URL.
  const buildBgImageEl = (dataUrl) =>
    new Promise((resolve) => {
      if (!dataUrl) { bgImageElRef.current = null; return resolve(null); }
      const img = new Image();
      img.onload = () => { bgImageElRef.current = img; resolve(img); };
      img.onerror = () => { bgImageElRef.current = null; resolve(null); };
      img.src = dataUrl;
    });

  // Attach/refresh the effects pipeline on the CURRENT camera track. Lazily
  // spins up the WASM processors only when at least one effect is on. If the
  // camera track was re-created (toggleCam), stale handles are disposed first.
  const applyEffectsToCurrentCam = useCallback(async (settings) => {
    if (!effectsSupported) return;
    const track = localTracksRef.current.camera;
    if (!track) return;
    if (effectsRef.current && effectsRef.current.track !== track) {
      try { await disposeEffects(effectsRef.current); } catch {}
      effectsRef.current = null;
    }
    const anyOn = (settings.background && settings.background !== "none") || settings.beautyEnabled;
    if (!effectsRef.current) {
      if (!anyOn) return;
      effectsRef.current = await createEffects(track, {
        onOverload: () => {
          toast.error("Video effects paused — your device is under heavy load.");
          const off = { ...getVideoEffects(), background: "none", beautyEnabled: false };
          setVideoEffectsState(off);
          setVideoEffects(off);
          if (effectsRef.current) applyEffects(effectsRef.current, off).catch(() => {});
        },
      });
    }
    let imageEl = null;
    if (settings.background === "image") {
      imageEl = bgImageElRef.current || (await buildBgImageEl(settings.imageDataUrl));
    }
    await applyEffects(effectsRef.current, { ...settings, imageEl });
  }, [effectsSupported]);

  // Panel change handler — updates state + persistence, then applies live.
  const updateVideoEffects = useCallback(async (patch) => {
    if (patch.imageDataUrl !== undefined) bgImageElRef.current = null;
    const next = { ...videoEffects, ...patch };
    setVideoEffectsState(next);
    setVideoEffects(next);
    try { await applyEffectsToCurrentCam(next); }
    catch (e) { console.warn("video effects apply failed", e); toast.error("Couldn't apply that video effect."); }
  }, [videoEffects, applyEffectsToCurrentCam]);

  // Apply effects once we've joined. If the user has NOT set a personal
  // preference, fall back to the per-workspace default (soft default —
  // applies to members + guests who haven't chosen their own effect).
  useEffect(() => {
    if (!joined || !effectsSupported) return;
    let s = videoEffects;
    if (!hasVideoEffectsPref()) {
      const wsd = joinInfo?.workspace_video_default;
      if (wsd) {
        const bg = wsd.background || "none";
        const beauty = !!wsd.beautyEnabled;
        if (bg !== "none" || beauty) {
          const built = { ...DEFAULT_VIDEO_EFFECTS, beautyEnabled: beauty };
          if (bg === "blur") {
            built.background = "blur";
            built.blurDegree = wsd.blurDegree || 2;
          } else if (bg === "preset" && wsd.presetId) {
            const src = resolvePresetSrc(wsd.presetId);
            if (src) { built.background = "image"; built.imageDataUrl = src; built.presetId = wsd.presetId; }
          }
          s = built;
          setVideoEffectsState(built);
        }
      }
    }
    if ((!s.background || s.background === "none") && !s.beautyEnabled) return;
    applyEffectsToCurrentCam(s).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined, effectsSupported]);

  // Agora STT decoder — attached as soon as we have an RTC client AND a
  // live transcription session. Listens for stream-message events from
  // the STT bot UID, decodes Protobuf payloads, and POSTs each final
  // utterance to /transcription/ingest. No secrets touch the browser.
  useAgoraSttDecoder({
    client: agoraClient,
    meetingId: joinInfo?.meeting_id,
    enabled: !!(sttSession && sttSession.status === "live"),
    botUid: sttSession?.bot_uid,
  });

  const toggleMic = async () => {
    const t = localTracksRef.current.audio;
    if (!t) {
      // The user joined with mic off (or it failed to acquire). Lazily
      // request the device now so they can turn the mic on mid-meeting.
      const client = clientRef.current;
      if (!client) return;
      try {
        const fresh = await AgoraRTC.createMicrophoneAudioTrack({});
        localTracksRef.current.audio = fresh;
        await client.publish(fresh);
        setMicOn(true);
      } catch (e) {
        console.error("Late mic acquire failed", e);
        toast.error("Could not access microphone");
      }
      return;
    }
    const next = !micOn;
    await t.setEnabled(next);
    setMicOn(next);
  };
  const toggleCam = async () => {
    const t = localTracksRef.current.camera;
    if (!t) {
      const client = clientRef.current;
      if (!client) return;
      try {
        const fresh = await AgoraRTC.createCameraVideoTrack({});
        localTracksRef.current.camera = fresh;
        // Re-attach any active video effects to the freshly-acquired track
        // before publishing so blur/beauty survive a camera off→on cycle.
        try {
          const s = videoEffects;
          if (effectsSupported && ((s.background && s.background !== "none") || s.beautyEnabled)) {
            await applyEffectsToCurrentCam(s);
          }
        } catch {}
        await client.publish(fresh);
        if (localVideoRef.current) try { fresh.play(localVideoRef.current); } catch {}
        setCamOn(true);
      } catch (e) {
        console.error("Late camera acquire failed", e);
        toast.error("Could not access camera");
      }
      return;
    }
    const next = !camOn;
    await t.setEnabled(next);
    setCamOn(next);
  };
  // v2.89 — Dedicated stop-only screen-share cleanup. Used by the
  // browser-native "Stop sharing this tab" track-ended handler so that
  // the stale-closure bug in toggleShare (which captures `sharing`
  // from the start-branch render where sharing===false, then takes
  // the START branch when invoked later) cannot reopen the screen
  // picker on a browser-native stop.
  //
  // This function reads ONLY mutable refs (`screenClientRef.current`,
  // `localTracksRef.current.*`) which always reflect the latest
  // values — there is no React state in the read path. setters
  // (setSharing, setLocalScreenUid, setRemoteSharerUid) don't depend
  // on state either. The function is fully idempotent: the early-
  // return guard means calling it twice (e.g., Bloq stop button + then
  // browser-native stop in close succession) is a no-op on the
  // second call. The Bloq stop button still routes through the
  // existing `toggleShare()` below — its behavior is unchanged.
  const stopShare = async () => {
    const screenClient = screenClientRef.current;
    const s = localTracksRef.current.screen;
    const sa = localTracksRef.current.screenAudio;
    if (!screenClient && !s) return; // not sharing — nothing to clean up
    if (screenClient) {
      try { if (s) await screenClient.unpublish(s); } catch {}
      try { if (sa) await screenClient.unpublish(sa); } catch {}
      try { await screenClient.leave(); } catch {}
      screenClientRef.current = null;
    }
    if (s)  { try { s.stop();  s.close();  } catch {} }
    if (sa) { try { sa.stop(); sa.close(); } catch {} }
    localTracksRef.current.screen = null;
    localTracksRef.current.screenAudio = null;
    setSharing(false);
    setLocalScreenUid(null);
    setRemoteSharerUid(null);
    try {
      await api.post(`/meetings/${joinInfo.meeting_id}/control-events`, {
        type: "screen_share_stopped",
        target_participant_id: null,
        params: { uid: joinInfo.uid },
      });
    } catch {}
  };

  const toggleShare = async () => {
    const client = clientRef.current;
    if (!client) return;
    try {
      if (sharing) {
        // ── Stop sharing ─────────────────────────────────────────────────
        const screenClient = screenClientRef.current;
        const s = localTracksRef.current.screen;
        const sa = localTracksRef.current.screenAudio;
        if (screenClient) {
          try { if (s) await screenClient.unpublish(s); } catch {}
          try { if (sa) await screenClient.unpublish(sa); } catch {}
          try { await screenClient.leave(); } catch {}
          screenClientRef.current = null;
        }
        if (s) { try { s.stop(); s.close(); } catch {} }
        if (sa) { try { sa.stop(); sa.close(); } catch {} }
        localTracksRef.current.screen = null;
        localTracksRef.current.screenAudio = null;
        // Camera was never unpublished — it kept playing into `localVideoRef`
        // the whole time, so there's nothing to re-attach when sharing stops.
        setSharing(false);
        setLocalScreenUid(null);
        // Defensive clear — if our own screen_share_started event echoed
        // back and set remoteSharerUid on our own client (legacy bug),
        // clear it here so we don't get stranded in screen-share layout.
        setRemoteSharerUid(null);
        api.post(`/meetings/${joinInfo.meeting_id}/control-events`, {
          type: "screen_share_stopped",
          target_participant_id: null,
          params: { uid: joinInfo.uid },
        }).catch(() => {});
      } else {
        // ── Start sharing ────────────────────────────────────────────────
        // 1. Acquire the screen track. Try 1080p/auto-audio first, fall
        //    back to 720p/no-audio if the browser refuses the system audio.
        const tryCreate = async (opts, withAudio) => {
          return await AgoraRTC.createScreenVideoTrack(opts, withAudio);
        };
        let result;
        try {
          result = await tryCreate({ encoderConfig: "1080p_1" }, "auto");
        } catch (e1) {
          console.warn("Screen share 'auto' audio failed — falling back to 'disable':", e1);
          result = await tryCreate({ encoderConfig: "720p_1" }, "disable");
        }
        let screenTrack = null;
        let screenAudio = null;
        if (Array.isArray(result)) {
          screenTrack = result[0];
          screenAudio = result[1] || null;
        } else {
          screenTrack = result;
        }
        localTracksRef.current.screen = screenTrack;
        localTracksRef.current.screenAudio = screenAudio;
        // 2. Get a SECOND Agora token for a screen-share UID and join the
        //    same channel from a fresh client. The host's main client keeps
        //    its camera publish — so remote viewers continue to see the
        //    host's camera in the side rail while the screen appears as a
        //    new remote user (the primary tile).
        const guestSessionId = (() => {
          try { return localStorage.getItem("bloq_guest_session_id"); } catch { return null; }
        })();
        const { data: screenTok } = await api.post(`/agora/join`, {
          meeting_public_id: publicId,
          display_name: joinInfo.display_name || user?.name || "Sharer",
          guest_session_id: guestSessionId,
          screen_share: true,
        });
        const screenClient = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
        await screenClient.join(screenTok.app_id, screenTok.channel_name, screenTok.token, screenTok.uid);
        screenClientRef.current = screenClient;
        const toPublish = [screenTrack];
        if (screenAudio) toPublish.push(screenAudio);
        await screenClient.publish(toPublish);
        // Remember our own screen-share UID so the layout can filter it out
        // of the side strip (otherwise the main client sees it as a remote
        // user and would render our own screen track on top of our camera).
        setLocalScreenUid(screenTok.uid);
        // Local preview: play the screen track in the DEDICATED screen ref.
        // The camera ref keeps the camera track — they NEVER share a DOM
        // container. If the screen tile hasn't mounted yet, the dedicated
        // effect above re-plays the track on mount.
        if (localScreenVideoRef.current) {
          try { screenTrack.play(localScreenVideoRef.current); } catch {}
        }
        setSharing(true);
        // 3. Broadcast — tell all other clients that THIS UID (the screen
        //    client's uid) is the screen-share track, so they can promote
        //    it to the primary tile in the layout.
        api.post(`/meetings/${joinInfo.meeting_id}/control-events`, {
          type: "screen_share_started",
          target_participant_id: null,
          params: { uid: screenTok.uid, owner_uid: joinInfo.uid, owner_name: screenTok.owner_display_name },
        }).catch(() => {});
        screenTrack.on("track-ended", () => { stopShare(); });
      }
    } catch (e) {
      console.error("screen share error", e);
      const name = e?.name || "";
      const detail = e?.message || e?.code || String(e);
      let friendly = `Screen sharing failed: ${detail}`;
      if (name === "NotAllowedError" || /denied|permission/i.test(detail)) {
        friendly = "Screen sharing was cancelled or denied. Pick a window or tab in the screen picker.";
      } else if (name === "NotReadableError" || /in use/i.test(detail)) {
        friendly = "Couldn't capture the screen — your OS may be blocking screen access for the browser. Check System Settings → Screen Recording.";
      } else if (name === "NotSupportedError") {
        friendly = "Your browser doesn't support screen sharing. Use a recent Chrome, Edge, or Firefox.";
      }
      toast.error(friendly);
    }
  };

  const endForAll = async () => {
    if (!canModerateMeeting) return;
    if (!window.confirm("End this meeting for everyone?")) return;
    // FAST PATH — flip the meeting status FIRST so any new /agora/join is
    // refused with HTTP 410. Then redirect the host IMMEDIATELY (no await
    // on Agora cleanup) and let the cleanup run in the background. The
    // broadcast event is fire-and-forget so other participants receive
    // the redirect signal during their next 4s control-event poll.
    try {
      // 1. Status flip — must succeed before we redirect so other clients
      //    see "ended" if they hit /meetings/{id} mid-stream. We await
      //    this but it usually returns in <250ms.
      await api.patch(`/meetings/${joinInfo.meeting_id}`, { status: "ended" });
    } catch {
      toast.error("Could not end meeting");
      return;
    }
    // 2. Fire-and-forget broadcast (don't block redirect on it).
    api.post(`/meetings/${joinInfo.meeting_id}/host/end-for-all`).catch(() => {});
    toast.success("Meeting ended for all");
    // 3. Clear screen-share UI immediately so the "you are sharing" pill
    //    disappears the moment the user clicks End for all — regardless
    //    of how slow the Agora cleanup runs in the background.
    setSharing(false);
    setRemoteSharerUid(null);
    // 4. Background cleanup — local tracks, Agora leave, /agora/leave
    //    POST — all fire-and-forget so the navigate below runs fast.
    (async () => { try { await leave(); } catch {} })();
    // 5. Redirect NOW. Logged-in users go to dashboard; guests to thanks.
    if (user?.user_id) {
      navigate("/app", { replace: true });
    } else {
      navigate(`/m/${publicId}/thanks`, { replace: true });
    }
  };

  // Warn the host before leaving while a cloud recording is still
  // live. Prevents accidentally losing a recording because the tab
  // closed without clicking Stop. The backend's auto-finalize-on-end
  // safety net kicks in if they confirm anyway.
  useEffect(() => {
    if (!recording) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "Recording is still active. Closing this page will end the meeting and finalize the recording.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [recording]);

  // Live participant filtering. Some non-camera UIDs can show up in
  // `remoteUsers` and we want them off the tile grid:
  //   1. Screen-share publisher UID — known via `remoteSharerUid`
  //      (control event) or our own `localScreenUid` state.
  //   2. The Agora Cloud Recording bot UID (200001) — defensive; the
  //      recorder never publishes, but if it ever did we don't want
  //      a tile for it.
  //   3. The Agora STT bot UIDs — exposed in `sttSession.bot_uid`
  //      (the pubBot UID). The subBot doesn't publish so it never
  //      appears in `remoteUsers`.
  // CRITICAL: real-user UIDs are `abs(hash(user_id)) % (2^31-1)` which
  // ranges from 1 to ~2.1 billion. The previous "floor at 100,000"
  // filter excluded almost every real user — that's the bug.
  const liveRemoteUsers = useMemo(() => {
    const excluded = new Set();
    if (remoteSharerUid != null) excluded.add(Number(remoteSharerUid));
    if (localScreenUid != null) excluded.add(Number(localScreenUid));
    excluded.add(200001); // Agora Cloud Recording bot UID
    if (sttSession?.bot_uid != null) excluded.add(Number(sttSession.bot_uid));
    return (remoteUsers || []).filter((u) => !excluded.has(Number(u.uid)));
  }, [remoteUsers, remoteSharerUid, localScreenUid, sttSession]);

  // ── Deterministic participant ordering for the live grid ─────────
  // Per the URGENT REGRESSION fix: active-speaker rotation is GONE.
  // We use a stable deterministic order:
  //   1. Host first (whether local or remote)
  //   2. Local user (if not host)
  //   3. Remote participants in join order
  // Camera state does NOT control whether someone gets a tile — a
  // camera-off participant still gets an avatar tile, just no video.
  const orderedParticipants = useMemo(() => {
    const out = [];
    // Local user — always present unless we're a leftover ref
    if (joinInfo?.uid != null) {
      out.push({
        uid: Number(joinInfo.uid),
        isHost: !!joinInfo.is_host,
        isLocal: true,
        displayName: joinInfo.display_name,
        joinOrder: 0,
      });
    }
    liveRemoteUsers.forEach((u, idx) => {
      out.push({
        uid: Number(u.uid),
        isHost: false,
        isLocal: false,
        joinOrder: idx + 1,
      });
    });
    // Host bubble-up: if host is a remote user, move them to position 0.
    const hostIdx = out.findIndex((p) => p.isHost);
    if (hostIdx > 0) {
      const [host] = out.splice(hostIdx, 1);
      out.unshift(host);
    }
    return out;
  }, [joinInfo, liveRemoteUsers]);

  // Pagination — 9 tiles per page; deterministic, no rotation. State
  // (TILES_PER_PAGE, tilePage, layoutMode, tileDensity, videoFraming,
  // spotlightUid, layoutMenuOpen) is declared near the top of this
  // component so the camera-replay effect can reference them safely.

  // Effective spotlight target: user pin > host > local user > first
  // participant. Falls back gracefully when the pinned UID leaves the
  // meeting. NEVER reacts to active-speaker volume — this is strictly
  // deterministic per the layout spec.
  const effectiveSpotlightUid = useMemo(() => {
    if (spotlightUid != null && orderedParticipants.find((p) => Number(p.uid) === Number(spotlightUid))) {
      return Number(spotlightUid);
    }
    const host = orderedParticipants.find((p) => p.isHost);
    if (host) return Number(host.uid);
    const local = orderedParticipants.find((p) => p.isLocal);
    if (local) return Number(local.uid);
    return orderedParticipants[0] ? Number(orderedParticipants[0].uid) : null;
  }, [spotlightUid, orderedParticipants]);

  // Different layouts paginate at different sizes:
  //   - auto / tiled:  9 per page (3x3 grid max)
  //   - spotlight:     1 primary + up to 8 strip tiles -> 9 per page
  //   - sidebar:       1 primary + up to 5 column tiles -> 6 per page
  // Sharing mode has its own (separate) layout below and is NOT paginated.
  const isSpotlight = layoutMode === "spotlight";
  const isSidebar = layoutMode === "sidebar";
  const stripPerPage = isSidebar ? 5 : 8;
  const nonSpotlightOrdered = useMemo(() => {
    if (!isSpotlight && !isSidebar) return orderedParticipants;
    return orderedParticipants.filter((p) => Number(p.uid) !== Number(effectiveSpotlightUid));
  }, [orderedParticipants, effectiveSpotlightUid, isSpotlight, isSidebar]);
  const totalPages = useMemo(() => {
    if (isSpotlight || isSidebar) {
      return Math.max(1, Math.ceil(nonSpotlightOrdered.length / stripPerPage));
    }
    return Math.max(1, Math.ceil(orderedParticipants.length / TILES_PER_PAGE));
  }, [orderedParticipants.length, nonSpotlightOrdered.length, isSpotlight, isSidebar, stripPerPage]);
  // Clamp current page when participants leave so we don't get stuck on
  // an empty page. Also reset to first page when the layout mode changes.
  useEffect(() => {
    if (tilePage > totalPages - 1) setTilePage(Math.max(0, totalPages - 1));
  }, [tilePage, totalPages]);
  useEffect(() => { setTilePage(0); }, [layoutMode]);
  const visibleParticipants = useMemo(() => {
    const start = tilePage * TILES_PER_PAGE;
    return orderedParticipants.slice(start, start + TILES_PER_PAGE);
  }, [orderedParticipants, tilePage]);
  // Strip slice — used by Spotlight & Sidebar. Empty for Auto/Tiled.
  const visibleStripParticipants = useMemo(() => {
    if (!isSpotlight && !isSidebar) return [];
    const start = tilePage * stripPerPage;
    return nonSpotlightOrdered.slice(start, start + stripPerPage);
  }, [nonSpotlightOrdered, tilePage, stripPerPage, isSpotlight, isSidebar]);

  // ── Cloud recording layout sync ────────────────────────────────────
  // Whenever camera state, screen-share state, or participant list
  // changes, push a fresh layout to the live Agora Cloud Recording
  // session. Backend treats the call as a no-op when no recording is
  // active so we can fire-and-forget from any event handler.
  //
  // IMPORTANT: we never read state out of `remoteCamOn` here — that
  // cache can lag behind Agora's event stream during a fast cam-toggle
  // and would cause the recording to omit a participant whose camera
  // is actually on. Instead, we trust the actual `videoTrack` ref:
  // if Agora has handed us a live track for that UID, the user has
  // video. This is what fixed the "Joseph in the meeting but missing
  // from the recording" bug.
  const pushRecordingLayout = useCallback(async () => {
    if (!joinInfo?.meeting_id) return;
    // Recording uses a SEPARATE deterministic selection: include up to
    // 9 camera-on UIDs in host-then-join-order. Local page state never
    // leaks into the recording layout — they're decoupled per spec.
    const cameraOn = [];
    for (const p of orderedParticipants) {
      const isCamOn = p.isLocal
        ? !!(camOn && localTracksRef.current.camera)
        : !!liveRemoteUsers.find((u) => Number(u.uid) === Number(p.uid))?.videoTrack;
      if (isCamOn) cameraOn.push(Number(p.uid));
      if (cameraOn.length >= 9) break;
    }
    const screenUid = localScreenUid ?? remoteSharerUid ?? null;
    try {
      await api.post(`/meetings/${joinInfo.meeting_id}/recording/update-layout`, {
        visible_uids: cameraOn,
        screen_share_uid: screenUid != null ? Number(screenUid) : null,
      });
    } catch {/* fire-and-forget; backend 404 just means no live session */}
  }, [joinInfo, orderedParticipants, camOn, liveRemoteUsers, localScreenUid, remoteSharerUid]);

  // Auto-push layout updates when a relevant input changes — debounced
  // through useEffect's natural batching so we don't spam Agora. No
  // active-speaker volume triggers here — only structural changes (cam
  // toggle, screen share start/stop, join/leave).
  useEffect(() => {
    if (!recording) return; // only when cloud recording is live
    const t = setTimeout(() => { pushRecordingLayout(); }, 750);
    return () => clearTimeout(t);
  }, [recording, camOn, sharing, localScreenUid, remoteSharerUid, orderedParticipants, pushRecordingLayout]);

  const startRecording = async () => {
    if (!canModerateMeeting) return;
    if (!joinInfo?.cloud_recording_configured) {
      toast.error("Cloud recording storage is not configured.");
      return;
    }
    try {
      // Deterministic: host + camera-on participants in join order,
      // capped at 9. No active-speaker scoring.
      const visibleUids = [];
      for (const p of orderedParticipants) {
        const isCamOn = p.isLocal
          ? !!(camOn && localTracksRef.current.camera)
          : !!liveRemoteUsers.find((u) => Number(u.uid) === Number(p.uid))?.videoTrack;
        if (isCamOn) visibleUids.push(Number(p.uid));
        if (visibleUids.length >= 9) break;
      }
      const screenUid = localScreenUid ?? remoteSharerUid ?? null;
      const { data } = await api.post(`/meetings/${joinInfo.meeting_id}/recording/start`, {
        visible_uids: visibleUids,
        screen_share_uid: screenUid != null ? Number(screenUid) : null,
        hide_camera_off_tiles: false,
      });
      setRecording(true);
      toast.success("Cloud recording started");
      return data;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not start cloud recording";
      toast.error(msg);
    }
  };
  const stopRecording = async () => {
    if (!canModerateMeeting) return;
    setRecording(false); // optimistic flip — visual feedback is instant
    try {
      setRecordSaving(true);
      await api.post(`/meetings/${joinInfo.meeting_id}/recording/stop`);
      toast.success("Recording saved to meeting details");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not stop recording");
      setRecording(true); // revert if stop failed
    } finally {
      setRecordSaving(false);
    }
  };

  if (waitingRoom) {
    return (
      <WaitingRoomScreen
        meetingTitle={waitingRoom.meeting_title}
        hostName={waitingRoom.host_name}
        displayName={waitingRoom.display_name}
        onLeave={() => navigate(`/m/${publicId}`)}
      />
    );
  }

  if (error) {
    return (
      <div className="bloq-meeting-room flex items-center justify-center">
        <div className="bloq-card p-8 max-w-md text-center text-[#1a1a1f]">
          <BloqLogo size={24} />
          <p className="bloq-headline text-2xl mt-4">Can't join the room</p>
          <p className="text-sm text-[#4a4a52] mt-2 mb-5">{error}</p>
          <button onClick={() => navigate(`/m/${publicId}`)} className="bloq-btn-primary">
            Back to lobby
          </button>
        </div>
      </div>
    );
  }

  if (ended) {
    return <MeetingEnded meetingId={joinInfo?.meeting_id} publicId={publicId} elapsed={elapsed} />;
  }

  // Helper — resolve one ordered participant into a renderable tile object.
  // For local: synthetic uid="local" with the local camera track from ref.
  // For remote: live AgoraUser with a videoTrack ref (or { uid, videoTrack:null }
  // if the user is joined but hasn't published video yet).
  const tileForParticipant = (p) => {
    if (p.isLocal) {
      return { uid: "local", videoTrack: localTracksRef.current.camera, isLocal: true, displayName: p.displayName };
    }
    const ru = liveRemoteUsers.find((u) => Number(u.uid) === Number(p.uid));
    return ru ? ru : { uid: p.uid, videoTrack: null };
  };
  // `allTiles` derives from `visibleParticipants` (deterministic page
  // slice — see pagination block above). Used by the Auto / Tiled grid
  // and (during screen share) the side strip. Local user ALWAYS appears
  // with whatever local camera track we have; remote users render their
  // subscribed videoTrack (or fall through to the avatar state if no
  // track is present). No filtering by cameraOn — camera-off users
  // still get an avatar tile.
  const allTiles = visibleParticipants.map(tileForParticipant);
  // Spotlight & Sidebar derive primary + strip from the effective
  // spotlight UID + the paginated non-spotlight slice. Both are still
  // strictly deterministic (no active-speaker rotation).
  const spotlightParticipant = orderedParticipants.find(
    (p) => Number(p.uid) === Number(effectiveSpotlightUid)
  );
  const spotlightTile = spotlightParticipant ? tileForParticipant(spotlightParticipant) : null;
  const stripTiles = visibleStripParticipants.map(tileForParticipant);

  const fmt = (s) => `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s / 60) % 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="bloq-meeting-room flex flex-col" data-testid="meeting-room-page">
      {/* Unmute / Camera-on request banner — appears when the host asks
          this participant to enable a device. Participant must consent;
          we never force-enable mic or cam without their click. */}
      {unmuteRequest && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-[90vw] rounded-xl shadow-2xl"
          style={{ background: "rgba(20,20,26,0.96)", backdropFilter: "blur(24px)" }}
          data-testid="unmute-request-banner"
        >
          <div className="p-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[#005e7a] flex items-center justify-center text-white flex-shrink-0">
              {unmuteRequest.target === "cam" ? <Video className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white">
                {unmuteRequest.by_name} asked you to {unmuteRequest.target === "cam" ? "turn your camera on" : "unmute"}.
              </p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={async () => {
                    try {
                      if (unmuteRequest.target === "mic") { await toggleMic(); }
                      else { await toggleCam(); }
                    } finally { setUnmuteRequest(null); }
                  }}
                  className="bloq-btn-primary text-xs"
                  data-testid="unmute-request-accept"
                >
                  {unmuteRequest.target === "cam" ? "Turn camera on" : "Unmute"}
                </button>
                <button
                  onClick={() => setUnmuteRequest(null)}
                  className="bloq-btn-secondary text-xs"
                  data-testid="unmute-request-decline"
                >
                  Not now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Top bar */}
      <header className="px-5 py-3 flex items-center justify-between" style={{ background: "rgba(20,20,26,0.8)", backdropFilter: "blur(20px)" }}>
        <div className="flex items-center gap-3">
          <BloqLogo size={20} white />
          <div className="hidden sm:flex flex-col">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium truncate max-w-[280px]" data-testid="room-meeting-title">
                {joinInfo?.meeting_title || (joinInfo ? "Meeting in progress" : "Connecting…")}
              </span>
              {recording && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600/20 text-red-300 text-[10px] font-bold uppercase tracking-widest"
                  data-testid="recording-indicator"
                  title="Recording in progress"
                >
                  <span className="w-2 h-2 rounded-full bg-red-500 bloq-rec-pulse" />
                  REC
                </span>
              )}
              {sttSession?.status === "live" && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-bold uppercase tracking-widest"
                  data-testid="captions-indicator"
                  title="Full-meeting transcription is running. They continue when you switch panels — open the Transcription panel to stop."
                >
                  <Captions className="w-3 h-3" />
                  CC · ON
                </span>
              )}
            </div>
            <span className="text-[11px] text-white/60 font-mono">{fmt(elapsed)}</span>
          </div>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Hide the video grid on mobile when a side panel is open so the
            panel can use the full screen width. On sm+ both render
            side-by-side as before. Before this, on mobile, the aside
            (w-full + flex-shrink-0) and main (flex-1) both fought for
            full container width — the row overflowed the viewport and
            the right edge of the panel was clipped. */}
        <main className={`flex-1 p-3 sm:p-5 min-h-0 overflow-hidden relative ${sidePanel ? "hidden sm:block" : ""}`}>
          {/* Layout selector — top-right of main, only when not screen
              sharing (screen share has its own dedicated layout). Persists
              the user's choice to localStorage. Strictly affects LIVE UI;
              never touches recording layout state. */}
          {!someoneIsSharing && !joining && (
            <div className="absolute top-2 right-2 z-20" data-testid="meeting-layout-selector">
              <button
                type="button"
                onClick={() => setLayoutMenuOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-black/55 backdrop-blur-md text-white/85 text-xs hover:bg-black/70"
                title="Change layout"
                data-testid="meeting-layout-toggle"
                aria-expanded={layoutMenuOpen}
              >
                {layoutMode === "spotlight" ? <Maximize className="w-3.5 h-3.5" /> : <LayoutGrid className="w-3.5 h-3.5" />}
                <span className="capitalize">{layoutMode}</span>
              </button>
              {layoutMenuOpen && (
                <div
                  className="absolute right-0 top-full mt-1 w-56 bg-[#16161c] border border-white/10 shadow-2xl py-2 text-white/85 text-xs"
                  data-testid="meeting-layout-menu"
                  role="menu"
                >
                  <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-white/45">Layout</div>
                  {[
                    { id: "auto", label: "Auto", desc: "Balanced grid", Icon: LayoutGrid },
                    { id: "tiled", label: "Tiled", desc: "Equal tiles", Icon: LayoutGrid },
                    { id: "spotlight", label: "Spotlight", desc: "One large + strip", Icon: Maximize },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => { setLayoutMode(opt.id); setLayoutMenuOpen(false); }}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white/5 ${layoutMode === opt.id ? "text-white" : "text-white/75"}`}
                      data-testid={`meeting-layout-option-${opt.id}`}
                      role="menuitemradio"
                      aria-checked={layoutMode === opt.id}
                    >
                      <opt.Icon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="flex-1">
                        <span className="block">{opt.label}</span>
                        <span className="block text-[10px] text-white/45">{opt.desc}</span>
                      </span>
                      {layoutMode === opt.id && <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
                    </button>
                  ))}
                  <div className="my-1.5 border-t border-white/10" />
                  <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-white/45">Density</div>
                  <div className="px-3 pb-1.5 flex gap-1.5">
                    {[
                      { id: "comfortable", label: "Comfortable" },
                      { id: "compact", label: "Compact" },
                    ].map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => setTileDensity(d.id)}
                        className={`flex-1 px-2 py-1 text-[11px] ${tileDensity === d.id ? "bg-[#005e7a] text-white" : "bg-white/5 text-white/75 hover:bg-white/10"}`}
                        data-testid={`meeting-density-${d.id}`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                  <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-white/45">Video framing</div>
                  <div className="px-3 pb-2 flex gap-1.5">
                    {[
                      { id: "fill", label: "Fill tile" },
                      { id: "fit", label: "Fit full video" },
                    ].map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => setVideoFraming(f.id)}
                        className={`flex-1 px-2 py-1 text-[11px] ${videoFraming === f.id ? "bg-[#005e7a] text-white" : "bg-white/5 text-white/75 hover:bg-white/10"}`}
                        data-testid={`meeting-framing-${f.id}`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                  {(layoutMode === "spotlight" || layoutMode === "sidebar") && spotlightUid != null && (
                    <>
                      <div className="border-t border-white/10 my-1" />
                      <button
                        type="button"
                        onClick={() => { setSpotlightUid(null); setLayoutMenuOpen(false); }}
                        className="w-full px-3 py-1.5 text-left text-white/75 hover:bg-white/5 text-[11px]"
                        data-testid="meeting-spotlight-clear"
                      >
                        Clear pinned spotlight
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {joining ? (
            <div className="h-full flex items-center justify-center text-white/70">
              <div className="bloq-spinner" />
            </div>
          ) : someoneIsSharing ? (
            // Screen-share layout: primary shared screen on the right, tiles
            // stacked vertically on the left as small squares.
            <div
              className="bloq-meeting-grid bloq-meeting-grid--sharing h-full"
              data-layout="sharing"
              data-testid="meeting-grid"
            >
              <div className="bloq-meeting-tile--strip">
                {allTiles.map((t) => {
                  // Strip filter rules:
                  //   1. Hide the active sharer's PRIMARY tile (their full
                  //      stream is already in the right-hand primary area).
                  //   2. When WE are sharing, our screenClient joins as a
                  //      separate Agora UID and shows up here as a
                  //      "remote" — filter it out so our own screen track
                  //      doesn't duplicate on top of our camera tile.
                  //   3. Our local CAMERA tile stays in the strip even while
                  //      we share, so we always see ourselves.
                  if (!sharing && remoteSharerUid === t.uid) return null;
                  if (sharing && !t.isLocal && localScreenUid != null && t.uid === localScreenUid) return null;
                  return t.isLocal ? (
                    <div key="local" className="bloq-meeting-tile--mini">
                      <LocalTile ref={localVideoRef} name={joinInfo?.display_name || "You"} micOn={micOn} camOn={camOn} sharing={false} mini />
                    </div>
                  ) : (
                    <div key={t.uid} className="bloq-meeting-tile--mini">
                      <RemoteTile
                        user={t}
                        displayName={participantsByUid[t.uid]?.name || participantsByUid[t.uid]?.display_name}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="bloq-meeting-tile--primary" data-testid="meeting-tile-primary">
                {sharing ? (
                  // We are the sharer — render a DEDICATED screen tile that
                  // owns its own DOM container. Kept separate from the
                  // camera tile in the strip so the screen track never
                  // replaces the camera feed.
                  <div className="bloq-meeting-tile relative w-full h-full" data-testid="meeting-tile-local-screen">
                    <div ref={localScreenVideoRef} className="absolute inset-0 bg-black" />
                    <div className="absolute bottom-2 left-2 right-2 flex items-center gap-1.5 text-[11px] text-white/85 bg-black/50 px-1.5 py-0.5 rounded">
                      <ScreenShare className="w-3 h-3" />
                      <span className="truncate">{joinInfo?.display_name || "You"} (sharing)</span>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Find the remote sharer from the RAW remoteUsers
                        list — liveRemoteUsers intentionally excludes the
                        screen-share UID (it's not a camera tile), so we
                        must reach into the unfiltered set here or
                        participants would see an empty primary area. */}
                    {(() => {
                      const sharer = (remoteUsers || []).find((u) => Number(u.uid) === Number(remoteSharerUid));
                      if (!sharer) return null;
                      return (
                        <RemoteTile
                          key={sharer.uid}
                          user={sharer}
                          displayName={`${remoteSharerOwnerName || participantsByUid[sharer.uid]?.name || participantsByUid[sharer.uid]?.display_name || "Someone"} (sharing)`}
                        />
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          ) : (
            // Live (non-sharing) layout — switches between Auto / Tiled /
            // Spotlight / Sidebar. Screen-share has its OWN dedicated
            // branch above and is intentionally untouched.
            (() => {
              if (isSpotlight) {
                return (
                  <div
                    className="bloq-meeting-grid bloq-meeting-grid--spotlight h-full"
                    data-density={tileDensity}
                    data-framing={videoFraming}
                    data-layout="spotlight"
                    data-testid="meeting-grid"
                  >
                    <div className="bloq-meeting-tile--primary" data-testid="spotlight-primary">
                      {spotlightTile && (
                        spotlightTile.isLocal ? (
                          <LocalTile
                            key="local-primary"
                            ref={localVideoRef}
                            name={joinInfo?.display_name || "You"}
                            micOn={micOn}
                            camOn={camOn}
                            sharing={sharing}
                            spotlighted
                          />
                        ) : (
                          <RemoteTile
                            key={`remote-${spotlightTile.uid}-primary`}
                            user={spotlightTile}
                            displayName={participantsByUid[spotlightTile.uid]?.name || participantsByUid[spotlightTile.uid]?.display_name}
                            spotlighted
                          />
                        )
                      )}
                    </div>
                    <div className="bloq-meeting-tile--strip" data-testid="spotlight-strip">
                      {stripTiles.map((t) => (
                        <button
                          key={t.isLocal ? "local-strip" : `remote-${t.uid}-strip`}
                          onClick={() => setSpotlightUid(t.isLocal ? Number(joinInfo?.uid) : Number(t.uid))}
                          className="block w-full text-left cursor-pointer"
                          title="Click to spotlight"
                          data-testid={t.isLocal ? "spotlight-strip-local" : `spotlight-strip-${t.uid}`}
                        >
                          {t.isLocal ? (
                            <LocalTile ref={localVideoRef} name={joinInfo?.display_name || "You"} micOn={micOn} camOn={camOn} sharing={sharing} />
                          ) : (
                            <RemoteTile user={t} displayName={participantsByUid[t.uid]?.name || participantsByUid[t.uid]?.display_name} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }
              if (isSidebar) {
                return (
                  <div
                    className="bloq-meeting-grid bloq-meeting-grid--sidebar h-full"
                    data-density={tileDensity}
                    data-framing={videoFraming}
                    data-layout="sidebar"
                    data-testid="meeting-grid"
                  >
                    <div className="bloq-meeting-tile--primary" data-testid="sidebar-primary">
                      {spotlightTile && (
                        spotlightTile.isLocal ? (
                          <LocalTile
                            key="local-primary"
                            ref={localVideoRef}
                            name={joinInfo?.display_name || "You"}
                            micOn={micOn}
                            camOn={camOn}
                            sharing={sharing}
                            spotlighted
                          />
                        ) : (
                          <RemoteTile
                            key={`remote-${spotlightTile.uid}-primary`}
                            user={spotlightTile}
                            displayName={participantsByUid[spotlightTile.uid]?.name || participantsByUid[spotlightTile.uid]?.display_name}
                            spotlighted
                          />
                        )
                      )}
                    </div>
                    <div className="bloq-meeting-tile--column" data-testid="sidebar-column">
                      {stripTiles.map((t) => (
                        <button
                          key={t.isLocal ? "local-strip" : `remote-${t.uid}-strip`}
                          onClick={() => setSpotlightUid(t.isLocal ? Number(joinInfo?.uid) : Number(t.uid))}
                          className="block w-full text-left cursor-pointer"
                          title="Click to spotlight"
                          data-testid={t.isLocal ? "sidebar-column-local" : `sidebar-column-${t.uid}`}
                        >
                          {t.isLocal ? (
                            <LocalTile ref={localVideoRef} name={joinInfo?.display_name || "You"} micOn={micOn} camOn={camOn} sharing={sharing} />
                          ) : (
                            <RemoteTile user={t} displayName={participantsByUid[t.uid]?.name || participantsByUid[t.uid]?.display_name} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }
              // Auto / Tiled — both flow tiles through a single grid
              // container that picks the right column count from
              // `data-count`. 3 participants render as a single row of
              // three 16:9 tiles on desktop, and as 2 + 1 (centered
              // bottom) squares on mobile (≤640px override in index.css).
              //
              // Cap the data-count attribute at the per-page maximum (6
              // mobile, 9 desktop) so the CSS picks the correct grid
              // template. Anything beyond that is paginated below.
              const count = Math.min(allTiles.length || 1, TILES_PER_PAGE);
              return (
                <div className="bloq-meeting-grid-frame" data-testid="meeting-grid-frame">
                <div
                  className="bloq-meeting-grid"
                  data-count={String(count)}
                  data-density={tileDensity}
                  data-framing={videoFraming}
                  data-layout={layoutMode}
                  data-testid="meeting-grid"
                >
                  {allTiles.map((t) =>
                    t.isLocal ? (
                      <LocalTile key="local" ref={localVideoRef} name={joinInfo?.display_name || "You"} micOn={micOn} camOn={camOn} sharing={sharing} />
                    ) : (
                      <RemoteTile
                        key={`remote-${t.uid}`}
                        user={t}
                        displayName={participantsByUid[t.uid]?.name || participantsByUid[t.uid]?.display_name}
                      />
                    )
                  )}
                </div>
                </div>
              );
            })()
          )}
          {totalPages > 1 && !sharing && !remoteSharerUid && (
            <div
              className="absolute bottom-[5px] left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-md text-white/85 text-xs"
              data-testid="meeting-pagination"
            >
              <button
                onClick={() => setTilePage((p) => Math.max(0, p - 1))}
                disabled={tilePage === 0}
                className="px-2 py-0.5 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="meeting-pagination-prev"
                aria-label="Previous page of participants"
              >
                Prev
              </button>
              <span className="text-white/70" data-testid="meeting-pagination-label">
                Page {tilePage + 1} of {totalPages}
              </span>
              <button
                onClick={() => setTilePage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={tilePage >= totalPages - 1}
                className="px-2 py-0.5 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="meeting-pagination-next"
                aria-label="Next page of participants"
              >
                Next
              </button>
            </div>
          )}
          {/* NOTE: Transcription overlay intentionally removed — transcript
              lives only in the Transcription side panel, never on top of the
              video. (See Issues #2 and #3 in the audit batch.) */}
          {deviceError && (
            <div
              className="absolute top-3 left-1/2 -translate-x-1/2 max-w-md rounded-md bg-amber-500/20 border border-amber-500/40 text-amber-100 px-3 py-2 text-xs"
              data-testid="device-error-banner"
            >
              <div className="flex items-center justify-between gap-3">
                <span>{deviceError}</span>
                <button onClick={() => setDeviceError(null)} className="text-amber-200/80 hover:text-white">
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </main>

        {sidePanel && (
          <aside className="w-full sm:w-80 lg:w-96 flex-shrink-0 border-l border-white/5 flex flex-col" style={{ background: "#16161c" }} data-testid={`side-panel-${sidePanel}`}>
            <header className="px-4 py-3 flex items-center justify-between">
              <p className="text-sm font-semibold uppercase tracking-widest text-white/70">{sidePanel}</p>
              <button onClick={() => setSidePanel(null)} className="text-white/60 hover:text-white" data-testid="close-side-panel">
                <X className="w-4 h-4" />
              </button>
            </header>
            {sidePanel === "chat" && (
              <ChatPanel
                meetingId={joinInfo?.meeting_id}
                messages={chatMessages}
                onSendOptimistic={(m) => setChatMessages((cur) => [...cur, m])}
                displayName={user?.name || prefsRef.current?.displayName || "Guest"}
              />
            )}
            {sidePanel === "notes" && user?.user_id && (
              <NotesPanel meetingId={joinInfo?.meeting_id} workspaceId={joinInfo?.workspace_id} />
            )}
            {sidePanel === "tasks" && user?.user_id && (
              <TasksPanel
                meetingId={joinInfo?.meeting_id}
                // 🔧 Phase-A2b — pass the CALLER'S current workspace_id
                // (not joinInfo.workspace_id which is the HOST's). Tasks
                // are saved under the creator's own workspace via
                // TaskFormDialog (which also uses currentWorkspace), so
                // querying with the host's workspace_id never matches a
                // non-host participant's own task. See CHANGELOG v2.86.
                workspaceId={currentWorkspace?.workspace_id}
                reloadKey={tasksReloadKey}
                onOpenTask={(id) => setSelectedTaskId(id)}
                onNewTask={() => setShowNewTaskDialog(true)}
              />
            )}
            {sidePanel === "participants" && (
              <ParticipantsPanel
                meetingId={joinInfo?.meeting_id}
                publicId={publicId}
                isHost={joinInfo?.is_host}
                isCohost={!!joinInfo?.is_cohost}
                isWorkspaceMember={joinInfo?.is_workspace_member}
                meetingMeta={meetingMeta}
                joinInfo={joinInfo}
                inviterName={user?.name || ""}
                myParticipantId={joinInfo?.participant_id}
                myUid={joinInfo?.uid}
                micOn={micOn}
                camOn={camOn}
                remoteMicOn={remoteMicOn}
                remoteCamOn={remoteCamOn}
                remoteLevels={remoteLevels}
              />
            )}
            {sidePanel === "transcript" && (
              <LiveTranscriptPanel
                meetingId={joinInfo?.meeting_id}
                sttProvider={sttProvider}
                sttSession={sttSession}
                sttEntries={sttEntries}
                workspaceDefaultLanguage={sttWorkspaceDefaults.default_transcription_language}
                onStartFullMeeting={startAgoraStt}
                onStopFullMeeting={stopAgoraStt}
                canControlFullMeeting={canModerateMeeting || !!joinInfo?.is_workspace_member}
              />
            )}
            {sidePanel === "settings" && (
              <InMeetingDeviceSettings
                clientRef={clientRef}
                localTracksRef={localTracksRef}
                onMicChange={setMicOn}
                onCamChange={setCamOn}
                localVideoRef={localVideoRef}
                meetingId={joinInfo?.meeting_id}
                transcriptionOn={sttSession?.status === "live"}
                onToggleTranscription={() => {
                  if (sttSession?.status === "live") stopAgoraStt();
                  else startAgoraStt(sttWorkspaceDefaults.default_transcription_language);
                }}
                recording={recording}
                onToggleRecording={() => (recording ? stopRecording() : startRecording())}
                isHost={canModerateMeeting}
                cloudRecordingConfigured={!!joinInfo?.cloud_recording_configured}
              />
            )}
          </aside>
        )}
      </div>

      {/* Bottom controls */}
      {effectsOpen && effectsSupported && (
        <VideoEffectsPanel
          settings={videoEffects}
          onChange={updateVideoEffects}
          onClose={() => setEffectsOpen(false)}
        />
      )}
      <footer className="px-3 py-4 flex justify-center" style={{ background: "rgba(20,20,26,0.85)", backdropFilter: "blur(20px)" }}>
        <div className="bloq-meeting-controls flex items-center" data-testid="meeting-controls">
          <button onClick={toggleMic} className="bloq-icon-btn" data-active={String(micOn)} title="Mic" data-testid="toggle-mic">
            {micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
          </button>
          <button onClick={toggleCam} className="bloq-icon-btn" data-active={String(camOn)} title="Camera" data-testid="toggle-cam">
            {camOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
          </button>
          <button onClick={toggleShare} className="bloq-icon-btn" data-active={String(!sharing)} title="Share" data-testid="toggle-share">
            <ScreenShare className="w-5 h-5" />
          </button>
          {effectsSupported && (
            <button
              onClick={() => setEffectsOpen((v) => !v)}
              className="bloq-icon-btn"
              data-fx={(effectsOpen || (videoEffects.background && videoEffects.background !== "none") || videoEffects.beautyEnabled) ? "on" : "off"}
              title="Video effects"
              data-testid="toggle-video-effects"
            >
              <Wand2 className="w-5 h-5" />
            </button>
          )}
          <div className="w-px h-8 bg-white/10 mx-1" />
          <button
            onClick={() => {
              setSidePanel(sidePanel === "chat" ? null : "chat");
              setUnreadChatCount(0);
              try { window.sessionStorage.setItem(`bloq_chat_seen_count:${joinInfo?.meeting_id}`, String(chatTotal)); } catch {}
            }}
            className="bloq-icon-btn relative"
            data-testid="open-chat"
          >
            <MessageCircle className="w-5 h-5" />
            {unreadChatCount > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
                style={{ borderRadius: 0 }}
                data-testid="unread-chat-badge"
              >
                {unreadChatCount > 99 ? "99+" : unreadChatCount}
              </span>
            )}
          </button>
          {/* Notes & Tasks save into the meeting's workspace. Visible to
              ANY logged-in user inside the meeting — including non-host
              participants and external invitees who joined via the public
              link. Non-logged-in guests don't see these (they'd have no
              place to sync notes/tasks to). */}
          {user?.user_id && (
            <button onClick={() => setSidePanel(sidePanel === "notes" ? null : "notes")} className="bloq-icon-btn" data-testid="open-notes">
              <StickyNote className="w-5 h-5" />
            </button>
          )}
          {user?.user_id && (
            <button onClick={() => setSidePanel(sidePanel === "tasks" ? null : "tasks")} className="bloq-icon-btn" data-testid="open-tasks">
              <ListChecks className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={() => setSidePanel(sidePanel === "participants" ? null : "participants")}
            className="bloq-icon-btn relative"
            data-testid="open-participants"
          >
            <Users className="w-5 h-5" />
            {canModerateMeeting && waitingCount > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center"
                style={{ borderRadius: 0 }}
                data-testid="waiting-room-badge"
                title={`${waitingCount} waiting to join`}
              >
                {waitingCount > 99 ? "99+" : waitingCount}
              </span>
            )}
          </button>
          <button onClick={() => setSidePanel(sidePanel === "settings" ? null : "settings")} className="bloq-icon-btn" title="Audio &amp; video settings" data-testid="open-settings">
            <Settings className="w-5 h-5" />
          </button>
          {/* Transcription nav button is now a pure panel-open trigger. The
              ON/OFF lifecycle lives inside the panel so the toolbar button
              can't accidentally start / stop captions. We color it red ONLY
              while captions are actively capturing — matching the recording
              indicator pattern. Uses `data-recording` instead of
              `data-active` so OFF-but-not-recording renders in the neutral
              grey tone (the previous `data-active="false"` → red rule made
              the button look ON when it was OFF). */}
          <button
            onClick={() => setSidePanel(sidePanel === "transcript" ? null : "transcript")}
            className="bloq-icon-btn"
            data-recording={String(sttSession?.status === "live")}
            title={sttSession?.status === "live" ? "Captions live — open the panel" : "Open transcription panel"}
            data-testid="toggle-transcription"
          >
            <Captions className="w-5 h-5" />
          </button>
          {canModerateMeeting && (
            <button
              onClick={recording ? stopRecording : startRecording}
              className="bloq-icon-btn"
              style={recording ? { background: "rgba(220,38,38,0.95)" } : {}}
              title={recording ? "Stop recording" : "Start recording"}
              data-testid="toggle-recording"
              disabled={recordSaving}
            >
              {recording ? <StopCircle className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
            </button>
          )}
          <div className="w-px h-8 bg-white/10 mx-1" />
          <button onClick={leave} className="bloq-icon-btn" data-variant="leave" title="Leave" data-testid="leave-button">
            <PhoneOff className="w-5 h-5" />
          </button>
          {canModerateMeeting && (
            <button onClick={endForAll} className="ml-1 px-3 py-2 rounded-full text-xs font-medium bg-white/10 hover:bg-white/20 text-white" data-testid="end-for-all">
              End for all
            </button>
          )}
        </div>
      </footer>

      {/* Full-power task editor — shared with main /app/tasks. Opens when the
          user clicks an in-meeting task row or the "New" button. */}
      {showNewTaskDialog && (
        <TaskFormDialog
          open={showNewTaskDialog}
          onOpenChange={setShowNewTaskDialog}
          onCreated={() => {
            setShowNewTaskDialog(false);
            setSidePanel("tasks");
            // 🔧 Phase-A2b — bump key so the panel reloads instantly
            // rather than waiting up to 8s for the periodic poll.
            setTasksReloadKey(k => k + 1);
          }}
          labels={meetingLabels}
          members={members}
          defaults={{ related_meeting_id: joinInfo?.meeting_id }}
        />
      )}
      {selectedTaskId && (
        <TaskDetailDrawer
          taskId={selectedTaskId}
          onClose={() => setSelectedTaskId(null)}
          labels={meetingLabels}
          members={members}
        />
      )}
    </div>
  );
}

const LocalTile = forwardRef(function LocalTile({ name, micOn, camOn, sharing, spotlighted }, ref) {
  // Outer wrapper: stacks the video tile + the name label vertically so
  // the label always sits BELOW the video, never overlaid on it. The
  // inner `.bloq-meeting-tile` keeps its 16:9 aspect-ratio so the grid
  // continues to size itself correctly.
  return (
    <div
      className="bloq-meeting-tile-wrap"
      data-spotlighted={spotlighted ? "true" : "false"}
      data-testid="local-video-tile-wrap"
    >
      <div className="bloq-meeting-tile" data-testid="local-video-tile">
        <div ref={ref} className="w-full h-full" />
        {!camOn && !sharing && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-medium" style={{ background: "#005e7a" }}>
              {(name || "Y").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
            </div>
          </div>
        )}
      </div>
      <div className="bloq-meeting-tile-namebar flex items-center gap-2">
        <span className="bloq-meeting-tile-name truncate">
          {name} {sharing && "· Sharing"}
        </span>
        {!micOn && <span className="w-5 h-5 rounded-full flex items-center justify-center bg-[#c43838]/90 flex-shrink-0"><MicOff className="w-3 h-3 text-white" /></span>}
      </div>
    </div>
  );
});

function RemoteTile({ user, displayName, spotlighted }) {
  const name = (displayName || "").trim() || "Participant";
  const containerRef = useRef(null);
  // CRITICAL: the `videoTrack` ref can arrive AFTER the tile DOM node
  // mounts (Agora's `user-published` event fires once subscribe()
  // resolves, which is async). A bare `ref` callback only runs at
  // mount/unmount, so the original code never played late-arriving
  // tracks — that was the "random missing camera" bug. This effect
  // re-binds the track every time it changes, and Agora handles
  // .play() being called multiple times on the same track as a no-op.
  //
  // 🔧 v2.88 belt-and-suspenders — call .stop() right before .play() so
  // the Agora SDK explicitly detaches from any prior (possibly removed-
  // from-DOM) container before binding to the new one. This mirrors the
  // v2.88 local-camera fix and eliminates the class of "remote tile goes
  // black after a layout swap" glitches the SDK can otherwise exhibit
  // when its internal container ref points at an orphaned DOM node.
  // stop() is safe-to-call-when-not-playing — wrapped in try/catch.
  useEffect(() => {
    if (!containerRef.current) return;
    if (user?.videoTrack) {
      try { user.videoTrack.stop(); } catch { /* not-playing → ignore */ }
      try {
        user.videoTrack.play(containerRef.current);
      } catch (e) {
        // Agora occasionally rejects play() when the track was already
        // playing on a different node — that's fine, the next event
        // will re-bind. We don't surface this to the user.
        console.warn("[RemoteTile] play() rejected", e);
      }
    }
    return () => {
      // Best-effort cleanup. We deliberately DO NOT call .stop() here.
      // Cleanup fires when this RemoteTile unmounts — but on a layout
      // swap the NEXT RemoteTile (in the new tree) may have already
      // mounted and called .play(newContainer). A stop() in this
      // unmount cleanup would race with that and stop the freshly-
      // bound track. The stop() above (at the START of the mount-time
      // effect on the NEW instance) is the safe place to reset SDK
      // state.
    };
  }, [user?.uid, user?.videoTrack]);

  const hasVideo = !!user?.videoTrack;
  return (
    <div
      className="bloq-meeting-tile-wrap"
      data-spotlighted={spotlighted ? "true" : "false"}
      data-testid={`remote-video-tile-wrap-${user.uid}`}
    >
      <div className="bloq-meeting-tile" data-testid={`remote-video-tile-${user.uid}`}>
        <div ref={containerRef} className="w-full h-full" />
        {!hasVideo && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#1a1a1f] text-white/60 text-sm pointer-events-none">
            {/* Camera-off placeholder. Audio is still subscribed; this
                is purely visual when a remote user hasn't published
                video yet (e.g., joined with camera off). */}
            <div className="w-12 h-12 rounded-full bg-[#005e7a]/70 flex items-center justify-center text-white font-medium">
              {(name[0] || "?").toUpperCase()}
            </div>
          </div>
        )}
      </div>
      <div className="bloq-meeting-tile-namebar">
        <span
          className="bloq-meeting-tile-name truncate"
          data-testid={`remote-name-${user.uid}`}
        >
          {name}
        </span>
      </div>
    </div>
  );
}

function formatChatTimestamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true });
  if (sameDay) return time;
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${date} · ${time}`;
}

function ChatPanel({ meetingId, messages, onSendOptimistic, displayName }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState(messages || []);
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(null); // message_id whose picker is open
  const fileInputRef = useRef(null);
  // pending file: chosen but not yet attached — we send the text first
  // (so we have a message_id), then attach the file to that message.
  const [pendingFile, setPendingFile] = useState(null);
  // Stay in sync with the centralized chat poller (passed via `messages`).
  useEffect(() => { setMsgs(messages || []); }, [messages]);

  const getGuestSessionId = () => {
    try { return localStorage.getItem("bloq_guest_session_id"); } catch { return null; }
  };

  const send = async () => {
    const t = text.trim();
    if (!t && !pendingFile) return;
    if (!meetingId) return;
    try {
      const guestSessionId = getGuestSessionId();
      // Some text is required for a chat message; if the user attached a
      // file without typing, use the filename as the message body.
      const messageText = t || (pendingFile ? pendingFile.name : "");
      const { data } = await api.post(`/meetings/${meetingId}/chat`, {
        text: messageText,
        guest_session_id: guestSessionId,
        author_name: displayName,
      });
      let messageWithAttachment = { ...data, attachments: [], reactions: [] };
      // If a file is attached, upload it now that we know the message_id.
      if (pendingFile) {
        try {
          setUploading(true);
          const form = new FormData();
          form.append("file", pendingFile);
          if (guestSessionId) form.append("guest_session_id", guestSessionId);
          const r = await api.post(
            `/meetings/${meetingId}/chat/${data.message_id}/attachments`,
            form,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          messageWithAttachment.attachments = [r.data];
        } catch (e) {
          toast.error(
            e?.response?.data?.detail || "Could not attach file"
          );
        } finally {
          setUploading(false);
        }
      }
      setMsgs((m) => [...m, messageWithAttachment]);
      onSendOptimistic?.(messageWithAttachment);
      setText("");
      setPendingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send message");
    }
  };

  const onPickFile = (e) => {
    const f = (e.target.files || [])[0];
    if (f) setPendingFile(f);
  };

  const downloadAttachment = async (att) => {
    try {
      const r = await api.get(
        `/meetings/${meetingId}/chat/attachments/${att.attachment_id}/download`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: att.content_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = att.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  /**
   * Delete one of MY chat messages (or any message if I'm the meeting host).
   * Optimistic update — we mark the message as deleted locally first so the
   * UI is snappy, then call the backend. If the call fails we'd see the
   * original message reappear on the next 4s chat poller tick.
   */
  const deleteMessage = async (msg) => {
    if (!msg?.message_id) return;
    if (!window.confirm("Delete this message?")) return;
    const guestSessionId = getGuestSessionId();
    try {
      await api.delete(`/meetings/${meetingId}/chat/${msg.message_id}`, {
        params: guestSessionId ? { guest_session_id: guestSessionId } : {},
      });
      setMsgs((prev) =>
        prev.map((m) =>
          m.message_id === msg.message_id
            ? { ...m, is_deleted: true, text: "", attachments: [], reactions: [] }
            : m
        )
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete message");
    }
  };

  // Check whether the current viewer authored this message (drives the
  // visibility of the trash icon). The host can delete anything; the
  // author can delete their own. We don't have meeting role here so we
  // fall back to author-only — the backend enforces the host case.
  const isMine = (msg) => {
    if (user && msg.author_user_id === user.user_id) return true;
    const gs = getGuestSessionId();
    if (!user && gs && msg.author_guest_session_id === gs) return true;
    return false;
  };

  const toggleReaction = async (msg, emoji) => {    const guestSessionId = getGuestSessionId();
    try {
      const { data } = await api.post(
        `/meetings/${meetingId}/chat/${msg.message_id}/reactions`,
        { emoji, guest_session_id: guestSessionId, author_name: displayName }
      );
      // Optimistic: update local copy. We then rely on the chat poller
      // to reconcile counts within the next 4s tick.
      setMsgs((prev) => prev.map((m) => {
        if (m.message_id !== msg.message_id) return m;
        const reactions = Array.isArray(m.reactions) ? [...m.reactions] : [];
        const idx = reactions.findIndex((r) => r.emoji === emoji);
        const meIdentity = user?.user_id || guestSessionId;
        const meName = displayName || "You";
        if (data.toggled === "added") {
          if (idx === -1) {
            reactions.push({ emoji, count: 1, mine: true, users: [{ display_name: meName, user_id: user?.user_id || null, guest_session_id: user ? null : guestSessionId }] });
          } else {
            reactions[idx] = { ...reactions[idx], count: reactions[idx].count + 1, mine: true };
          }
        } else {
          if (idx !== -1) {
            const nextCount = reactions[idx].count - 1;
            if (nextCount <= 0) reactions.splice(idx, 1);
            else reactions[idx] = { ...reactions[idx], count: nextCount, mine: false };
          }
        }
        return { ...m, reactions };
      }));
      setPickerOpen(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not react");
    }
  };

  return (
    <div className="flex-1 flex flex-col p-3 overflow-hidden">
      <div className="flex-1 overflow-y-auto flex flex-col gap-2.5 pr-1">
        {msgs.length === 0 ? (
          <p className="text-xs text-white/50 text-center mt-6">No messages yet. Start the conversation.</p>
        ) : msgs.map((m) => (
          <div key={m.message_id} className="bg-white/5 p-2.5 group relative" style={{ borderRadius: 0 }} data-testid={`chat-msg-${m.message_id}`}>
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <p className="text-[11px] text-white/70 font-medium">{m.author_name}</p>
              <p className="text-[10px] text-white/40">{formatChatTimestamp(m.created_at)}</p>
            </div>
            {m.is_deleted ? (
              // Soft-deleted placeholder. We keep the message bubble (so
              // the chat order stays stable) but strip everything except a
              // dim "Message deleted" line. Reactions + attachments are
              // already cleared server-side.
              <p
                className="text-xs text-white/40 italic"
                data-testid={`chat-msg-deleted-${m.message_id}`}
              >
                Message deleted
              </p>
            ) : (
              <ChatText text={m.text} className="text-sm text-white/90" />
            )}
            {/* Attachments */}
            {!m.is_deleted && Array.isArray(m.attachments) && m.attachments.length > 0 && (
              <div className="mt-1.5 flex flex-col gap-1.5">
                {m.attachments.map((att) => (
                  <button
                    key={att.attachment_id}
                    onClick={() => downloadAttachment(att)}
                    className="flex items-center gap-2 px-2 py-1.5 bg-white/5 hover:bg-white/15 text-left text-xs text-white/90 transition-colors"
                    style={{ borderRadius: 0 }}
                    data-testid={`chat-attachment-${att.attachment_id}`}
                    title={`Download ${att.file_name}`}
                  >
                    <FileText className="w-3.5 h-3.5 flex-shrink-0 text-[#a3d8e6]" />
                    <span className="flex-1 truncate">{att.file_name}</span>
                    <span className="text-[10px] text-white/50">{formatBytes(att.file_size)}</span>
                    <Download className="w-3 h-3 flex-shrink-0 text-white/60" />
                  </button>
                ))}
              </div>
            )}
            {/* Reactions row */}
            {!m.is_deleted && Array.isArray(m.reactions) && m.reactions.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`chat-reactions-${m.message_id}`}>
                {m.reactions.map((r) => (
                  <button
                    key={r.emoji}
                    onClick={() => toggleReaction(m, r.emoji)}
                    className={`px-1.5 py-0.5 text-[11px] inline-flex items-center gap-1 transition-colors ${r.mine ? "bg-[#005e7a]/40 ring-1 ring-[#a3d8e6]" : "bg-white/10 hover:bg-white/20"}`}
                    style={{ borderRadius: 0 }}
                    title={(r.users || []).map((u) => u.display_name).join(", ")}
                    data-testid={`chat-reaction-${m.message_id}-${r.emoji}`}
                  >
                    <span>{r.emoji}</span>
                    <span className="text-white/80">{r.count}</span>
                  </button>
                ))}
              </div>
            )}
            {/* Hover actions: React + Delete (author/host only).
                Both hide for soft-deleted messages so users can't react
                to or re-delete a tombstone. */}
            {!m.is_deleted && (
              <div className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                <button
                  onClick={() => setPickerOpen(pickerOpen === m.message_id ? null : m.message_id)}
                  className="p-1 bg-white/10 hover:bg-white/20"
                  style={{ borderRadius: 0 }}
                  title="Add reaction"
                  aria-label="Add reaction"
                  data-testid={`chat-react-btn-${m.message_id}`}
                >
                  <Smile className="w-3.5 h-3.5 text-white" />
                </button>
                {isMine(m) && (
                  <button
                    onClick={() => deleteMessage(m)}
                    className="p-1 bg-white/10 hover:bg-[#c43838]/40"
                    style={{ borderRadius: 0 }}
                    title="Delete message"
                    aria-label="Delete message"
                    data-testid={`chat-delete-btn-${m.message_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-white" />
                  </button>
                )}
              </div>
            )}
            {!m.is_deleted && pickerOpen === m.message_id && (
              <div
                className="absolute right-1 top-full mt-1 z-10 bg-[#1a1a1f] border border-white/15 p-1.5 grid grid-cols-5 gap-0.5 shadow-lg"
                style={{ borderRadius: 0 }}
                data-testid={`chat-react-picker-${m.message_id}`}
              >
                {DEFAULT_REACTIONS.map((e) => (
                  <button
                    key={e}
                    onClick={() => toggleReaction(m, e)}
                    className="w-7 h-7 inline-flex items-center justify-center hover:bg-white/15 text-base"
                    style={{ borderRadius: 0 }}
                    data-testid={`chat-react-option-${m.message_id}-${e}`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Pending-attachment preview */}
      {pendingFile && (
        <div
          className="mt-2 px-2 py-1.5 bg-white/10 flex items-center gap-2 text-xs text-white/90"
          style={{ borderRadius: 0 }}
          data-testid="chat-pending-file"
        >
          <Paperclip className="w-3.5 h-3.5" />
          <span className="flex-1 truncate">{pendingFile.name}</span>
          <span className="text-[10px] text-white/50">{formatBytes(pendingFile.size)}</span>
          <button
            onClick={() => { setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
            className="text-white/60 hover:text-white"
            aria-label="Remove attachment"
            data-testid="chat-pending-file-remove"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      <div className="flex gap-2 mt-3 items-center">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={onPickFile}
          data-testid="chat-file-input"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="px-2 py-2 text-white/80 hover:text-white hover:bg-white/10"
          style={{ borderRadius: 0 }}
          title="Attach a file"
          aria-label="Attach a file"
          data-testid="chat-attach-button"
          disabled={uploading}
        >
          <Paperclip className="w-4 h-4" />
        </button>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={pendingFile ? "Add a message (optional)…" : "Say something…"}
          className="flex-1 bg-white/5 text-white text-sm px-3 py-2 outline-none focus:bg-white/10"
          style={{ borderRadius: 0 }}
          data-testid="chat-input"
        />
        <button
          onClick={send}
          disabled={uploading || (!text.trim() && !pendingFile)}
          className="px-3 py-2 bg-[#005e7a] text-white disabled:opacity-50"
          style={{ borderRadius: 0 }}
          data-testid="chat-send"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function ParticipantsPanel({ meetingId, publicId, isHost, isCohost, isWorkspaceMember, meetingMeta, joinInfo, inviterName, myParticipantId, myUid, micOn, camOn, remoteMicOn, remoteCamOn, remoteLevels }) {
  const [list, setList] = useState([]);
  const [waiting, setWaiting] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  // Join URL is the actual room URL the user is already on. Falls back to
  // the canonical PUBLIC_URL if window.location is unavailable (SSR/test).
  const joinUrl = (typeof window !== "undefined" && window.location?.origin && publicId)
    ? `${window.location.origin}/m/${publicId}`
    : "";
  // Lock controls if the meeting has ended. The status poller in the
  // parent will navigate away on `ended`, but we still defensively hide
  // the controls here so a stale render can't expose the link.
  const meetingStatus = (meetingMeta?.status || "").toLowerCase();
  const meetingEnded = meetingStatus === "ended" || meetingStatus === "completed" || meetingStatus === "canceled";
  // Only host / co-host / workspace member can dispatch invite emails
  // through the Bloq email pipeline. Everyone else still sees Copy Link
  // and Open-in-Email-App.
  //
  // v2.93 — `isHost` / `isCohost` props come from `joinInfo`, which is
  // set ONCE at meeting-join time and never refreshed. If the local user
  // was promoted to co-host AFTER joining, `joinInfo.is_cohost` stays
  // stale at `false`. The participants poll (every 4s) DOES refresh the
  // role flags per row, so we derive the local user's current role from
  // their own participant row whenever the list updates. `isHost` /
  // `isCohost` props remain as boot-time fallbacks (used for the first
  // ~4s before the poll arrives).
  const myRow = list.find((p) => p.participant_id === myParticipantId);
  const liveIsHost = myRow ? !!myRow.is_host : !!isHost;
  const liveIsCohost = myRow ? !!myRow.is_cohost : !!isCohost;
  const canSendInvites = !!(liveIsHost || liveIsCohost || isWorkspaceMember);
  const canModerate = !!(liveIsHost || liveIsCohost);
  const copyLink = async () => {
    if (!joinUrl) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(joinUrl);
      } else {
        // Fallback for browsers without async clipboard API
        const ta = document.createElement("textarea");
        ta.value = joinUrl;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Meeting link copied.");
    } catch {
      toast.error("Couldn't copy. Select the link and copy it manually.");
    }
  };
  useEffect(() => {
    if (!meetingId) return;
    const load = async () => {
      try { const { data } = await api.get(`/meetings/${meetingId}/participants`); setList(data); } catch {}
      if (canModerate) {
        try {
          const { data } = await api.get(`/waiting-room/meeting/${meetingId}`);
          setWaiting((data || []).filter((w) => w.status === "waiting"));
        } catch { /* not authorized — guests don't see this list */ }
      }
    };
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [meetingId, canModerate]);
  const active = list.filter((p) => !p.left_at);
  const left = list.filter((p) => p.left_at);
  const mute = async (p) => {
    try { await api.post(`/meetings/${meetingId}/participants/${p.participant_id}/mute`, { target: "audio" }); toast.success(`Mute signal sent to ${p.name}`); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not mute"); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.name} from the meeting?`)) return;
    try { await api.post(`/meetings/${meetingId}/participants/${p.participant_id}/remove`, { reason: "Removed by host" }); toast.success(`Remove signal sent to ${p.name}`); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not remove"); }
  };
  // v2.90 — promote/demote co-host. Backend gates on `_user_can_moderate`
  // so any moderator (host OR co-host) can invoke these endpoints. The
  // frontend kebab menu hides the buttons when the local user isn't a
  // moderator, but the backend is the actual source of truth.
  const promoteCohost = async (p) => {
    if (!p.user_id) return; // guests cannot be co-hosts
    try {
      await api.post(`/meetings/${meetingId}/cohosts`, { user_id: p.user_id });
      toast.success(`${p.name} is now a co-host`);
      // Optimistic local update; the 4s poll will refresh from backend anyway.
      setList((arr) => arr.map((x) => x.participant_id === p.participant_id ? { ...x, is_cohost: true } : x));
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not promote"); }
  };
  const demoteCohost = async (p) => {
    if (!p.user_id) return;
    if (!window.confirm(`Remove co-host status from ${p.name}?`)) return;
    try {
      await api.delete(`/meetings/${meetingId}/cohosts/${p.user_id}`);
      toast.success(`${p.name} is no longer a co-host`);
      setList((arr) => arr.map((x) => x.participant_id === p.participant_id ? { ...x, is_cohost: false } : x));
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not demote"); }
  };
  const admit = async (w) => {
    try { await api.post(`/waiting-room/admit/${w.wr_id}`); toast.success(`${w.display_name || "Guest"} admitted`); setWaiting((arr) => arr.filter((x) => x.wr_id !== w.wr_id)); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not admit"); }
  };
  const admitAll = async () => {
    try {
      const { data } = await api.post(`/waiting-room/admit-all/${meetingId}`);
      toast.success(`Admitted ${data?.admitted ?? "all"} waiting`);
      setWaiting([]);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not admit all"); }
  };
  const deny = async (w) => {
    if (!window.confirm(`Deny ${w.display_name || "Guest"} entry?`)) return;
    try { await api.post(`/waiting-room/deny/${w.wr_id}`); toast.success("Denied"); setWaiting((arr) => arr.filter((x) => x.wr_id !== w.wr_id)); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not deny"); }
  };
  const liveStateFor = (p) => {
    if (p.participant_id === myParticipantId) {
      return { micOn, camOn, level: 0, isMe: true };
    }
    const uid = p.uid;
    return {
      micOn: remoteMicOn?.[uid] !== false,
      camOn: remoteCamOn?.[uid] === true,
      level: remoteLevels?.[uid] || 0,
      isMe: false,
    };
  };
  return (
    <div className="flex-1 flex flex-col p-3 overflow-y-auto">
      {/* Waiting room — moderators only (host + co-hosts), visible while
          someone is waiting. v2.90 — expanded from host-only to moderator
          gate since backend admit/deny/admit-all already accept co-hosts. */}
      {canModerate && waiting.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3" data-testid="waiting-room-panel">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-xs font-semibold text-amber-200 uppercase tracking-widest">Waiting to join ({waiting.length})</p>
            <button
              onClick={admitAll}
              className="text-[11px] font-medium bg-amber-500/30 hover:bg-amber-500/40 text-amber-100 px-2 py-1 rounded"
              data-testid="waiting-admit-all"
            >
              Admit all
            </button>
          </div>
          <ul className="flex flex-col gap-1.5">
            {waiting.map((w) => (
              <li key={w.wr_id} className="flex items-center gap-2 p-2 rounded bg-white/5" data-testid={`waiting-row-${w.wr_id}`}>
                <Avatar className="w-7 h-7 rounded-md">
                  <AvatarFallback className="bg-amber-500/40 text-amber-100 text-[10px] rounded-md">
                    {(w.display_name || "G").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white/90 truncate">{w.display_name || "Guest"}</p>
                </div>
                <button
                  onClick={() => admit(w)}
                  className="text-[11px] font-medium bg-emerald-600/80 hover:bg-emerald-600 text-white px-2 py-1 rounded"
                  data-testid={`waiting-admit-${w.wr_id}`}
                >
                  Admit
                </button>
                <button
                  onClick={() => deny(w)}
                  className="text-[11px] font-medium bg-white/10 hover:bg-red-500/40 text-white/80 px-2 py-1 rounded"
                  data-testid={`waiting-deny-${w.wr_id}`}
                >
                  Deny
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Global controls — broadcast to every participant. v2.90 —
          expanded from host-only to moderator gate since backend
          mute-all / cam-off-all / unmute-request-all all accept co-hosts. */}
      {canModerate && active.length > 1 && (
        <div className="mb-3 flex flex-wrap gap-1.5" data-testid="host-global-controls">
          <button
            onClick={async () => {
              if (!window.confirm("Mute everyone in the meeting?")) return;
              try { await api.post(`/meetings/${meetingId}/host/mute-all`); toast.success("Mute-all sent"); }
              catch (e) { toast.error(e?.response?.data?.detail || "Could not mute all"); }
            }}
            className="text-[11px] font-medium bg-white/10 hover:bg-white/20 text-white px-2 py-1 rounded inline-flex items-center gap-1"
            data-testid="host-mute-all"
          ><MicOff className="w-3 h-3" /> Mute all</button>
          <button
            onClick={async () => {
              try { await api.post(`/meetings/${meetingId}/host/unmute-request-all`, { target: "mic" }); toast.success("Unmute request sent to everyone"); }
              catch (e) { toast.error(e?.response?.data?.detail || "Could not request unmute"); }
            }}
            className="text-[11px] font-medium bg-white/10 hover:bg-white/20 text-white px-2 py-1 rounded inline-flex items-center gap-1"
            data-testid="host-request-unmute-all"
          ><Mic className="w-3 h-3" /> Request unmute all</button>
          <button
            onClick={async () => {
              if (!window.confirm("Turn off every participant's camera?")) return;
              try { await api.post(`/meetings/${meetingId}/host/cam-off-all`); toast.success("Cam-off-all sent"); }
              catch (e) { toast.error(e?.response?.data?.detail || "Could not turn off cameras"); }
            }}
            className="text-[11px] font-medium bg-white/10 hover:bg-white/20 text-white px-2 py-1 rounded inline-flex items-center gap-1"
            data-testid="host-cam-off-all"
          ><VideoOff className="w-3 h-3" /> Cams off all</button>
        </div>
      )}

      <p className="text-xs text-white/60 mb-2">In the meeting ({active.length})</p>
      <ul className="flex flex-col gap-2">
        {active.map((p) => (
          <ParticipantRow
            key={p.participant_id}
            p={p}
            active
            live={liveStateFor(p)}
            canModerate={canModerate && p.participant_id !== myParticipantId}
            isCohostRow={!!p.is_cohost}
            isHostRow={!!p.is_host}
            canPromoteCohost={canModerate && !!p.user_id && !p.is_host && !p.is_cohost && p.participant_id !== myParticipantId}
            canDemoteCohost={canModerate && !!p.user_id && !p.is_host && !!p.is_cohost && p.participant_id !== myParticipantId}
            onPromoteCohost={() => promoteCohost(p)}
            onDemoteCohost={() => demoteCohost(p)}
            onMute={async () => {
              try { await api.post(`/meetings/${meetingId}/participants/${p.participant_id}/mute`, { target: "audio" }); toast.success(`Muted ${p.name}`); }
              catch (e) { toast.error(e?.response?.data?.detail || "Could not mute"); }
            }}
            onCamOff={async () => {
              try { await api.post(`/meetings/${meetingId}/participants/${p.participant_id}/cam-off`); toast.success(`Cam off for ${p.name}`); }
              catch (e) { toast.error(e?.response?.data?.detail || "Could not turn off cam"); }
            }}
            onRequestUnmute={async () => {
              try { await api.post(`/meetings/${meetingId}/participants/${p.participant_id}/unmute-request`, { target: "mic" }); toast.success(`Unmute request sent to ${p.name}`); }
              catch (e) { toast.error(e?.response?.data?.detail || "Could not request unmute"); }
            }}
            onRequestCamOn={async () => {
              try { await api.post(`/meetings/${meetingId}/participants/${p.participant_id}/unmute-request`, { target: "cam" }); toast.success(`Camera-on request sent to ${p.name}`); }
              catch (e) { toast.error(e?.response?.data?.detail || "Could not request camera"); }
            }}
            onRemove={() => remove(p)}
          />
        ))}
        {active.length === 0 && <li className="text-xs text-white/40 text-center py-3">Waiting for participants…</li>}
      </ul>
      {left.length > 0 && (
        <>
          <p className="text-xs text-white/60 mt-5 mb-2">Already left ({left.length})</p>
          <ul className="flex flex-col gap-2">
            {left.map((p) => <ParticipantRow key={p.participant_id} p={p} />)}
          </ul>
        </>
      )}

      {/* Meeting link + Invite — rendered at the bottom of the panel.
          Hidden once the meeting has ended so a stale link can't be shared
          for a room that no longer accepts joins. */}
      {!meetingEnded && joinUrl && (
        <section className="mt-5 p-3 bg-white/5 border border-white/10" data-testid="meeting-link-section">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-xs font-semibold text-white/70 uppercase tracking-widest inline-flex items-center gap-1.5">
              <LinkIcon className="w-3 h-3" /> Meeting link
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={joinUrl}
              onFocus={(e) => e.target.select()}
              className="flex-1 min-w-0 bg-black/30 text-white/90 text-xs px-2 py-1.5 border border-white/10 outline-none truncate"
              data-testid="meeting-link-input"
            />
            <button
              onClick={copyLink}
              className="text-[11px] font-medium bg-white/10 hover:bg-white/20 text-white px-2 py-1.5 inline-flex items-center gap-1"
              data-testid="meeting-link-copy"
              title="Copy meeting link"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              onClick={() => setShowInvite(true)}
              className="text-[11px] font-medium bg-emerald-600/80 hover:bg-emerald-600 text-white px-2.5 py-1.5 inline-flex items-center gap-1.5"
              data-testid="meeting-link-invite"
            >
              <UserPlus className="w-3 h-3" /> Invite
            </button>
            <p className="text-[10px] text-white/40 leading-tight">
              {canSendInvites
                ? "Anyone with the link can request access; waiting room (if on) still applies."
                : "Share the link — only the host can email invites from Bloq."}
            </p>
          </div>
        </section>
      )}

      {showInvite && (
        <InviteModal
          meetingId={meetingId}
          joinUrl={joinUrl}
          meeting={meetingMeta}
          joinInfo={joinInfo}
          inviterName={inviterName}
          canSendInvites={canSendInvites}
          onClose={() => setShowInvite(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// In-meeting Invite modal — opened from the Meeting Link section above.
// Lives in the Participants panel area; never touches other meeting state.
// ---------------------------------------------------------------------------
function InviteModal({ meetingId, joinUrl, meeting, joinInfo, inviterName, canSendInvites, onClose }) {
  const [draft, setDraft] = useState("");
  const [emails, setEmails] = useState([]);          // chips: [{ email, valid: bool, error?: string }]
  const [sending, setSending] = useState(false);
  // results: list of { email, status, error? } returned by the backend
  const [results, setResults] = useState(null);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValid = (e) => EMAIL_RE.test(e);

  // Tokenize draft into chips. Splits on comma/whitespace/semicolons.
  // Dedupes against existing chips (case-insensitive). Invalid tokens
  // stay in the chip list with `valid:false` so the user can see + fix
  // them — sending will refuse to dispatch invalid entries.
  const commitDraft = (raw) => {
    const text = (raw ?? draft) || "";
    const parts = text.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!parts.length) {
      setDraft("");
      return;
    }
    setEmails((cur) => {
      const seen = new Set(cur.map((c) => c.email.toLowerCase()));
      const next = [...cur];
      for (const p of parts) {
        if (seen.has(p.toLowerCase())) continue;
        seen.add(p.toLowerCase());
        next.push({ email: p, valid: isValid(p) });
      }
      return next;
    });
    setDraft("");
  };

  const removeChip = (idx) => setEmails((cur) => cur.filter((_, i) => i !== idx));

  const validEmails = emails.filter((c) => c.valid).map((c) => c.email);
  const invalidCount = emails.filter((c) => !c.valid).length;
  const hasAny = emails.length > 0 || draft.trim().length > 0;

  // Compose the mailto body — Open in Email App is the same content the
  // Bloq email would send, minus the wrapper styling.
  const meetingTitle = meeting?.title || joinInfo?.meeting_title || "Bloq meeting";
  const hostName = meeting?.host_name || meeting?.host?.name || "—";
  const tz = meeting?.timezone || joinInfo?.meeting_timezone || "UTC";
  const startIso = meeting?.actual_start || joinInfo?.meeting_actual_start || meeting?.start_time || joinInfo?.meeting_start_time || null;
  let localDate = "";
  let localTime = "";
  try {
    if (startIso) {
      const d = new Date(startIso);
      if (!isNaN(d.getTime())) {
        localDate = d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
        localTime = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
      }
    }
  } catch { /* ignore — fall back to blanks */ }
  if (!localDate) localDate = "Now";
  if (!localTime) localTime = "Now";

  const description = (meeting?.description || "").trim();

  const buildMailto = () => {
    const subject = `Invite: ${meetingTitle}`;
    const lines = [
      "Hi,",
      "",
      "You're invited to join this meeting in session.",
      "",
      `Meeting: ${meetingTitle}`,
      `Invited by: ${inviterName || "A meeting participant"}`,
      `Host: ${hostName}`,
      `Date: ${localDate}`,
      `Time: ${localTime}`,
      `Timezone: ${tz}`,
    ];
    if (description) {
      lines.push("");
      lines.push("Description:");
      lines.push(description);
    }
    lines.push("");
    lines.push("Join Meeting:");
    lines.push(joinUrl || "");
    const body = lines.join("\n");
    const to = validEmails.join(",");
    return `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const openInEmailApp = () => {
    // Commit any pending draft into chips so it ends up in the recipient
    // list before we hand off to the OS email client.
    commitDraft();
    // Open in a NEW window/tab so the user stays in the live meeting —
    // navigating the current window away from /m/{publicId} would drop
    // them from Agora. `window.open` is the only reliable cross-browser
    // way to fire a mailto handoff without leaving the page. We discard
    // the returned window handle because the OS email client takes over
    // immediately and the spawned tab closes itself on most browsers.
    window.open(buildMailto(), "_blank", "noopener,noreferrer");
    // No backend log — per spec this is NOT "sent" by the platform.
  };

  const sendInvites = async () => {
    commitDraft();
    // After commit, re-read the chip list from state. We need to wait a
    // microtask so React's setEmails has applied. Easiest path: also
    // tokenize the current draft locally so we never race with state.
    const tokens = (draft || "").split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    const merged = (() => {
      const seen = new Set(emails.map((c) => c.email.toLowerCase()));
      const next = [...emails];
      for (const t of tokens) {
        if (seen.has(t.toLowerCase())) continue;
        seen.add(t.toLowerCase());
        next.push({ email: t, valid: isValid(t) });
      }
      return next;
    })();
    const toSend = merged.filter((c) => c.valid).map((c) => c.email);
    if (!toSend.length) {
      toast.error("Add at least one valid email address.");
      return;
    }
    if (!canSendInvites) {
      toast.error("Only the host or a workspace member can email invites. Use 'Open in Email App' instead.");
      return;
    }
    setSending(true);
    setResults(null);
    try {
      const { data } = await api.post(`/meetings/${meetingId}/invite`, { emails: toSend });
      setResults(data?.results || []);
      const sent = data?.sent ?? 0;
      const failed = data?.failed ?? 0;
      const skipped = data?.skipped ?? 0;
      if (sent > 0 && failed === 0 && skipped === 0) {
        toast.success(`Invite sent to ${sent} ${sent === 1 ? "person" : "people"}.`);
      } else if (sent > 0) {
        toast.success(`Sent to ${sent}. ${failed} failed, ${skipped} skipped.`);
      } else if (data?.provider === "mock") {
        toast.error("Email provider isn't configured — nothing was sent. Use 'Open in Email App' instead.");
      } else {
        toast.error(`Couldn't send invites (${failed} failed, ${skipped} skipped).`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't send invites.";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="invite-modal"
    >
      <div className="bg-[#1a1a1f] border border-white/10 w-full max-w-md max-h-[90vh] overflow-y-auto text-white shadow-xl">
        <header className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <h3 className="text-sm font-semibold inline-flex items-center gap-2">
            <UserPlus className="w-4 h-4" /> Invite to this meeting
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/10 rounded"
            data-testid="invite-modal-close"
          ><X className="w-4 h-4" /></button>
        </header>
        <div className="p-4 flex flex-col gap-3">
          <p className="text-xs text-white/60">
            Enter one or more email addresses. Press <kbd className="px-1 py-0.5 bg-white/10 rounded text-[10px]">Enter</kbd>, comma, or space to add each address.
          </p>

          <div
            className="min-h-[44px] bg-white/5 border border-white/10 px-2 py-2 flex flex-wrap gap-1.5 focus-within:border-emerald-500/60"
            onClick={() => document.getElementById("invite-email-input")?.focus()}
          >
            {emails.map((c, i) => (
              <span
                key={`${c.email}-${i}`}
                className={`inline-flex items-center gap-1 text-[12px] px-2 py-0.5 ${
                  c.valid
                    ? "bg-emerald-600/20 text-emerald-200 border border-emerald-500/40"
                    : "bg-red-500/20 text-red-200 border border-red-500/40"
                }`}
                data-testid={`invite-chip-${i}`}
                title={c.valid ? c.email : `Invalid email — ${c.email}`}
              >
                {c.valid ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                <span className="max-w-[180px] truncate">{c.email}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); removeChip(i); }}
                  className="ml-0.5 -mr-0.5 opacity-70 hover:opacity-100"
                  data-testid={`invite-chip-remove-${i}`}
                  aria-label={`Remove ${c.email}`}
                ><X className="w-3 h-3" /></button>
              </span>
            ))}
            <ContactAutocomplete
              id="invite-email-input"
              type="email"
              dark
              workspaceId={joinInfo?.workspace_id || meeting?.workspace_id}
              value={draft}
              onChange={setDraft}
              onSelect={(c) => {
                // Picking a contact → tokenize their email into a chip
                // using the existing chip-commit pipeline. Reset draft so
                // the same suggestion can't double-commit.
                if (c?.email) commitDraft(c.email);
                setDraft("");
              }}
              onBlur={() => commitDraft()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === " " || e.key === "Tab") {
                  if (draft.trim()) {
                    e.preventDefault();
                    commitDraft();
                  }
                } else if (e.key === "Backspace" && !draft && emails.length) {
                  e.preventDefault();
                  removeChip(emails.length - 1);
                }
              }}
              placeholder={emails.length ? "" : "name@example.com"}
              wrapperClassName="flex-1 min-w-[120px]"
              className="w-full bg-transparent outline-none text-sm text-white placeholder-white/30 py-0.5"
              testid="invite-email-input"
            />
          </div>
          {invalidCount > 0 && (
            <p className="text-[11px] text-red-300" data-testid="invite-invalid-hint">
              {invalidCount} {invalidCount === 1 ? "address looks" : "addresses look"} invalid — remove or fix to send.
            </p>
          )}

          {/* Per-recipient results after a send attempt. */}
          {results && results.length > 0 && (
            <ul className="text-[11px] flex flex-col gap-1 bg-black/30 border border-white/10 p-2" data-testid="invite-results">
              {results.map((r, i) => (
                <li
                  key={`${r.email}-${i}`}
                  className="flex items-center justify-between gap-2"
                  data-testid={`invite-result-${i}`}
                >
                  <span className="truncate text-white/80">{r.email}</span>
                  <span className={
                    r.status === "sent"
                      ? "text-emerald-300"
                      : r.status === "duplicate"
                        ? "text-white/50"
                        : "text-red-300"
                  }>
                    {r.status === "sent" && <>✓ Sent</>}
                    {r.status === "duplicate" && <>Duplicate — skipped</>}
                    {r.status === "invalid_email" && <>Invalid email</>}
                    {r.status === "provider_not_configured" && <>Email provider not configured</>}
                    {r.status === "failed" && <>Failed{r.error ? ` — ${r.error}` : ""}</>}
                    {!["sent", "duplicate", "invalid_email", "provider_not_configured", "failed"].includes(r.status) && r.status}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {!canSendInvites && (
            <p className="text-[11px] text-amber-300/90 leading-snug" data-testid="invite-permission-hint">
              Only the host or workspace members can email invites from Bloq. You can still share the link directly or use <strong>Open in Email App</strong> below.
            </p>
          )}
        </div>

        <footer className="flex flex-wrap items-center justify-end gap-2 px-4 py-3 border-t border-white/10">
          <button
            onClick={onClose}
            className="text-[12px] font-medium bg-transparent hover:bg-white/10 text-white/70 px-3 py-1.5"
            data-testid="invite-modal-cancel"
          >Cancel</button>
          <button
            onClick={openInEmailApp}
            disabled={!hasAny && !joinUrl}
            className="text-[12px] font-medium bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-40"
            data-testid="invite-modal-mailto"
            title="Open your default email app with this invite pre-filled"
          >
            <Mail className="w-3.5 h-3.5" /> Open in Email App
          </button>
          <button
            onClick={sendInvites}
            disabled={sending || validEmails.length === 0 || !canSendInvites}
            className="text-[12px] font-medium bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="invite-modal-send"
            title={canSendInvites ? "Send invite emails via Bloq" : "Hosts and workspace members only"}
          >
            <Send className="w-3.5 h-3.5" /> {sending ? "Sending…" : "Send Invite"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function ParticipantRow({ p, active, live, canModerate, isCohostRow, isHostRow, canPromoteCohost, canDemoteCohost, onPromoteCohost, onDemoteCohost, onMute, onCamOff, onRequestUnmute, onRequestCamOn, onRemove }) {
  const initials = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const speaking = active && live && live.micOn && (live.level || 0) > 4;
  const showMicCam = active && live;
  return (
    <li
      className="flex items-center gap-3 px-2 py-2 rounded-lg bg-white/5"
      data-testid={`participant-${p.participant_id}`}
      data-speaking={speaking ? "true" : "false"}
    >
      <Avatar
        className={`w-8 h-8 rounded-md transition-shadow ${speaking ? "ring-2 ring-emerald-400" : ""}`}
      >
        <AvatarImage src={p.avatar_url || ""} className="rounded-md" />
        <AvatarFallback className="bg-[#005e7a] text-white text-[10px] rounded-md">{initials(p.name)}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        {/* Line 1: name only — gets the full row width so long names simply
            truncate instead of colliding with the badge / icons. (v2.95d) */}
        <p className="text-sm text-white/90 truncate">
          <span className="align-middle">{p.name}</span>
          {live?.isMe && <span className="text-[10px] text-white/40 ml-1 align-middle">(you)</span>}
        </p>
        {/* Line 2: role badge + live mic/cam indicators, moved BELOW the name
            (v2.95d UI fix) so they never overlap a long name. */}
        <div className="flex items-center gap-1.5 mt-0.5">
          {isHostRow && (
            <span
              className="shrink-0 text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 bg-amber-500/20 text-amber-300 border border-amber-400/30 rounded"
              data-testid={`role-badge-host-${p.participant_id}`}
            >Host</span>
          )}
          {isCohostRow && !isHostRow && (
            <span
              className="shrink-0 text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 bg-teal-500/15 text-teal-300 border border-teal-400/30 rounded"
              data-testid={`role-badge-cohost-${p.participant_id}`}
            >Co-host</span>
          )}
          {/* Live mic / cam indicators — only on active rows. */}
          {showMicCam && (
            <div className="flex items-center gap-1 shrink-0" data-testid={`indicator-${p.participant_id}`}>
              <span
                className={`w-5 h-5 rounded-full flex items-center justify-center ${
                  live.micOn ? (speaking ? "bg-emerald-500/30 text-emerald-300" : "bg-white/10 text-white/70") : "bg-red-500/30 text-red-200"
                }`}
                title={live.micOn ? (speaking ? "Speaking" : "Mic on") : "Muted"}
                data-testid={`indicator-mic-${p.participant_id}`}
                data-mic={live.micOn ? "on" : "off"}
              >
                {live.micOn ? <Mic className="w-3 h-3" /> : <MicOff className="w-3 h-3" />}
              </span>
              <span
                className={`w-5 h-5 rounded-full flex items-center justify-center ${
                  live.camOn ? "bg-white/10 text-white/70" : "bg-red-500/30 text-red-200"
                }`}
                title={live.camOn ? "Camera on" : "Camera off"}
                data-testid={`indicator-cam-${p.participant_id}`}
                data-cam={live.camOn ? "on" : "off"}
              >
                {live.camOn ? <Video className="w-3 h-3" /> : <VideoOff className="w-3 h-3" />}
              </span>
            </div>
          )}
          {active && !showMicCam && <span className="w-2 h-2 rounded-full bg-[#16a34a] shrink-0" title="In meeting" />}
          <span className="text-[10px] text-white/40 truncate min-w-0">{p.role}</span>
        </div>
      </div>
      {canModerate && (
        <div className="flex items-center gap-1">
          {/* v2.90 — co-host promote/demote. Visible only when the local
              user can moderate AND the target is eligible (workspace
              member, not the host, not self). The handler is a no-op for
              guests since they don't have a user_id; we render the
              button in a disabled state via the parent's gate logic. */}
          {canPromoteCohost && (
            <button onClick={onPromoteCohost} title="Make co-host" className="p-1 rounded hover:bg-white/10 text-teal-300" data-testid={`promote-cohost-${p.participant_id}`}>
              <UserPlus className="w-3.5 h-3.5" />
            </button>
          )}
          {canDemoteCohost && (
            <button onClick={onDemoteCohost} title="Remove co-host" className="p-1 rounded hover:bg-white/10 text-white/70" data-testid={`demote-cohost-${p.participant_id}`}>
              <UserMinus className="w-3.5 h-3.5" />
            </button>
          )}
          {live?.micOn ? (
            <button onClick={onMute} title="Force-mute mic" className="p-1 rounded hover:bg-white/10 text-white/70" data-testid={`mute-${p.participant_id}`}>
              <MicOff className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button onClick={onRequestUnmute} title="Ask to unmute" className="p-1 rounded hover:bg-white/10 text-white/70" data-testid={`request-unmute-${p.participant_id}`}>
              <Mic className="w-3.5 h-3.5" />
            </button>
          )}
          {live?.camOn ? (
            <button onClick={onCamOff} title="Turn off camera" className="p-1 rounded hover:bg-white/10 text-white/70" data-testid={`cam-off-${p.participant_id}`}>
              <VideoOff className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button onClick={onRequestCamOn} title="Ask to turn camera on" className="p-1 rounded hover:bg-white/10 text-white/70" data-testid={`request-cam-on-${p.participant_id}`}>
              <Video className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={onRemove} title="Remove from meeting" className="p-1 rounded hover:bg-white/10 text-red-400" data-testid={`remove-${p.participant_id}`}>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </li>
  );
}

function NotesPanel({ meetingId, workspaceId }) {
  const [notes, setNotes] = useState([]);
  const [content, setContent] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    if (!workspaceId) return;
    const { data } = await api.get(`/notes`, { params: { workspace_id: workspaceId, meeting_id: meetingId } });
    setNotes(data);
  }, [meetingId, workspaceId]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    const c = content.trim();
    if (!c) return;
    setSaving(true);
    try {
      await api.post("/notes", { content: c, meeting_id: meetingId, workspace_id: workspaceId });
      setContent("");
      await load();
    } catch { toast.error("Could not save note"); }
    finally { setSaving(false); }
  };
  const saveEdit = async (id) => {
    const c = editingText.trim();
    if (!c) return;
    try {
      await api.patch(`/notes/${id}`, { content: c });
      setEditingId(null);
      setEditingText("");
      load();
    } catch { toast.error("Could not save edit"); }
  };
  const del = async (id) => { await api.delete(`/notes/${id}`); load(); };
  return (
    <div className="flex-1 flex flex-col p-3 overflow-hidden">
      <p className="text-xs text-white/60 mb-2">Notes here sync to your Notes tab and to Recall.</p>
      <div className="flex-1 overflow-y-auto flex flex-col gap-2 pr-1">
        {notes.length === 0 ? (
          <p className="text-xs text-white/50 text-center mt-6">No notes yet.</p>
        ) : notes.map((n) => (
          <div key={n.note_id} className="rounded-lg bg-white/5 p-2.5 group">
            {editingId === n.note_id ? (
              <div className="flex flex-col gap-2">
                <textarea
                  rows={3}
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  className="bg-white/10 text-white text-sm px-2 py-1.5 rounded outline-none resize-none"
                />
                <div className="flex justify-end gap-2">
                  <button onClick={() => { setEditingId(null); setEditingText(""); }} className="text-xs text-white/60 hover:text-white">Cancel</button>
                  <button onClick={() => saveEdit(n.note_id)} className="text-xs text-[#005e7a] font-medium bg-white/20 hover:bg-white/30 px-2 py-1 rounded">Save</button>
                </div>
              </div>
            ) : (
              <div className="flex justify-between gap-2">
                <p className="text-sm text-white/90 whitespace-pre-wrap break-words flex-1">{n.content}</p>
                <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => { setEditingId(n.note_id); setEditingText(n.content); }} className="text-white/40 hover:text-white" data-testid={`edit-note-${n.note_id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => del(n.note_id)} className="text-white/40 hover:text-red-400" data-testid={`delete-note-${n.note_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <textarea
          rows={2}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) add(); }}
          placeholder="Jot it down… (⌘/Ctrl + Enter to save)"
          className="flex-1 bg-white/5 text-white text-sm px-3 py-2 rounded-lg outline-none focus:bg-white/10 resize-none"
          data-testid="note-input"
        />
        <button onClick={add} disabled={saving} className="px-3 py-2 rounded-lg bg-[#005e7a] text-white disabled:opacity-50" data-testid="note-save">
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function TasksPanel({ meetingId, workspaceId, reloadKey = 0, onOpenTask, onNewTask }) {
  // Real task system — opens the SAME TaskDetailDrawer / TaskFormDialog used
  // by the main Tasks app. Don't render an in-meeting-only mini editor.
  //
  // 🔧 Phase-A2b: `workspaceId` is the CALLER'S current workspace, not the
  // host's. This matches what TaskFormDialog uses when creating in-meeting
  // tasks, so a non-host participant's task (saved under their own
  // workspace_id) is correctly fetched by their own panel. `reloadKey`
  // bumps when a task is created so we refresh instantly instead of
  // waiting up to 8s for the periodic poll.
  const [tasks, setTasks] = useState([]);
  const load = useCallback(async () => {
    if (!workspaceId) return;
    const { data } = await api.get(`/tasks`, {
      params: { workspace_id: workspaceId, meeting_id: meetingId, include_archived: false },
    });
    setTasks(data);
  }, [meetingId, workspaceId]);
  useEffect(() => { load(); }, [load, reloadKey]);
  // Bi-directional sync — edits in the main Tasks tab show up here too.
  useEffect(() => {
    if (!workspaceId) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load, workspaceId]);
  // Toggle uses the REAL task status enum (todo ↔ done). Sync with main app.
  const toggle = async (task) => {
    const nextStatus = task.status === "done" ? "todo" : "done";
    try {
      await api.patch(`/tasks/${task.task_id}`, { status: nextStatus });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update task");
    }
  };

  const PRIORITY_BADGE = {
    urgent: { bg: "bg-[#dc2626]/30", c: "text-[#fecaca]" },
    high:   { bg: "bg-[#f97316]/30", c: "text-[#fed7aa]" },
    medium: { bg: "bg-[#3b82f6]/30", c: "text-[#bfdbfe]" },
    low:    { bg: "bg-[#10b981]/30", c: "text-[#a7f3d0]" },
  };

  return (
    <div className="flex-1 flex flex-col p-3 overflow-hidden">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-white/60">Linked to this meeting — synced with main Tasks.</p>
        <button
          onClick={onNewTask}
          className="text-xs font-medium bg-[#005e7a] hover:bg-[#005e7a]/90 text-white px-2.5 py-1 rounded inline-flex items-center gap-1"
          data-testid="task-new"
        >
          <Plus className="w-3 h-3" /> New
        </button>
      </div>
      <div className="flex-1 overflow-y-auto flex flex-col gap-2 pr-1 min-h-0">
        {tasks.length === 0 ? (
          <p className="text-xs text-white/50 text-center mt-6">No tasks yet for this meeting.</p>
        ) : tasks.map((t) => {
          const done = t.status === "done";
          const p = PRIORITY_BADGE[t.priority] || PRIORITY_BADGE.medium;
          const overdue = !done && t.due_date && new Date(t.due_date) < new Date();
          return (
            <div
              key={t.task_id}
              role="button"
              tabIndex={0}
              onClick={() => onOpenTask?.(t.task_id)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onOpenTask?.(t.task_id); }}
              className="rounded-lg bg-white/5 p-2.5 flex items-start gap-2 group cursor-pointer hover:bg-white/10 transition-colors"
              data-testid={`task-row-${t.task_id}`}
            >
              <input
                type="checkbox"
                checked={done}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggle(t)}
                className="mt-1 accent-[#005e7a]"
                data-testid={`task-checkbox-${t.task_id}`}
              />
              <div className="flex-1 min-w-0">
                <p className={`text-sm break-words ${done ? "line-through text-white/40" : "text-white/90"}`}>{t.title}</p>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${p.bg} ${p.c}`}>{t.priority}</span>
                  <span className="text-[10px] text-white/40">{t.status}</span>
                  {t.due_date && (
                    <span className={`text-[10px] ${overdue ? "text-red-300 font-semibold" : "text-white/40"}`}>
                      due {new Date(t.due_date).toLocaleDateString()}
                    </span>
                  )}
                  {t.assignee_name && (
                    <span className="text-[10px] text-white/40">→ {t.assignee_name}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-white/40 mt-3 text-center">Click a task to open the full editor.</p>
    </div>
  );
}

function InMeetingRecallPanel({ meetingId }) {
  const [m, setM] = useState(null);
  const [notes, setNotes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [generating, setGenerating] = useState(false);
  const load = useCallback(async () => {
    if (!meetingId) return;
    try {
      const r = await api.get(`/recall/meeting/${meetingId}`);
      setM(r.data.meeting);
      setNotes(r.data.notes || []);
      setSummary(r.data.summary || null);
      setTranscript(r.data.transcript || null);
      setRecordings(r.data.recordings || []);
    } catch {}
  }, [meetingId]);
  useEffect(() => { load(); }, [load]);
  // Refresh Recall data while in the room so a freshly-saved note shows up.
  useEffect(() => {
    if (!meetingId) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load, meetingId]);
  const generateSummary = async () => {
    if (!meetingId) return;
    if (m && m.ai_summary_enabled === false) {
      toast.error("AI summary is disabled for this meeting. Enable it in Meeting Settings.");
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post(`/summaries/generate`, { meeting_id: meetingId });
      setSummary(r.data);
      toast.success("Summary generated and saved to Recall");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not generate summary";
      toast.error(msg);
    } finally { setGenerating(false); }
  };
  if (!m) return <div className="text-xs text-white/50 p-3">Loading…</div>;
  const aiEnabled = m.ai_summary_enabled !== false;
  const aiCaptured = !!(summary?.summary_text || "").trim();
  const transcriptionEnabled = !!m.transcription_enabled;
  const transcriptCaptured = !!(transcript?.full_text || "").trim();
  const recordingEnabled = !!m.recording_enabled;
  const recordingCaptured = recordings.length > 0;
  return (
    <div className="flex-1 flex flex-col p-3 overflow-y-auto">
      <p className="text-xs text-white/60 mb-3">
        Recall captures notes, transcript, AI summary, tasks, and participants and bundles them into
        a downloadable Recall Report after the meeting ends.
      </p>
      <div className="flex flex-col gap-2.5">
        <DarkStatusRow
          label="AI summary"
          enabled={aiEnabled}
          captured={aiCaptured}
          testid="recall-status-ai-summary"
        />
        <DarkStatusRow
          label="Transcription"
          enabled={transcriptionEnabled || transcriptCaptured}
          captured={transcriptCaptured}
          hint="Browser transcription captures your microphone only. Other participants may not be transcribed unless a server-side transcription provider is configured."
          testid="recall-status-transcription"
        />
        <DarkStatusRow
          label="Recording"
          enabled={recordingEnabled || recordingCaptured}
          captured={recordingCaptured}
          testid="recall-status-recording"
        />
      </div>
      <button
        onClick={generateSummary}
        disabled={generating || !aiEnabled}
        className="mt-4 bloq-btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="generate-summary-btn"
      >
        <Sparkles className="w-4 h-4" />
        {generating ? "Generating…" : summary?.summary_text ? "Regenerate AI summary" : "Generate AI summary"}
      </button>
      {summary?.summary_text && (
        <div className="mt-4 rounded-lg bg-white/5 p-3">
          <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Latest summary</p>
          <p className="text-xs text-white/90 whitespace-pre-wrap leading-relaxed line-clamp-6">{summary.summary_text}</p>
          <p className="text-[10px] text-white/40 mt-2">Open the meeting after the call ends to view the full Recall.</p>
        </div>
      )}
      <div className="mt-5 pt-4 border-t border-white/5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-widest text-white/40">Notes ({notes.length})</p>
          <p className="text-[10px] text-white/40">stored under this meeting</p>
        </div>
        {notes.length === 0 ? (
          <p className="text-xs text-white/40">No notes yet. Open the Notes panel to add one — it'll appear here.</p>
        ) : (
          <div className="flex flex-col gap-1.5 max-h-44 overflow-y-auto pr-1" data-testid="recall-notes-list">
            {notes.slice(0, 8).map((n) => (
              <div key={n.note_id} className="rounded-md bg-white/5 p-2">
                <p className="text-xs text-white/90 whitespace-pre-wrap line-clamp-3">{n.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="mt-5 pt-4 border-t border-white/5">
        <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">After this meeting</p>
        <p className="text-xs text-white/70 leading-relaxed">
          Open the meeting record page to download the Recall Report (DOCX or PDF) with section toggles.
          Notes are <strong className="text-white/90">excluded</strong> from the Recall Report by default — you can opt them in.
        </p>
      </div>
    </div>
  );
}

function DarkStatusRow({ label, enabled, captured, hint, testid }) {
  return (
    <div className="rounded-lg bg-white/5 p-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium">{label}</p>
        <div className="flex items-center gap-1.5">
          <span
            className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${
              enabled ? "bg-[#005e7a]/40 text-[#bae6fd]" : "bg-white/10 text-white/60"
            }`}
            data-testid={`${testid}-enabled`}
          >
            {enabled ? "Enabled" : "Disabled"}
          </span>
          <span
            className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${
              captured ? "bg-[#16a34a]/40 text-[#bbf7d0]" : "bg-white/10 text-white/60"
            }`}
            data-testid={`${testid}-captured`}
          >
            {captured ? "Captured" : "Not Captured"}
          </span>
        </div>
      </div>
      {hint && <p className="text-[11px] text-white/50 mt-1 leading-snug">{hint}</p>}
    </div>
  );
}

function InMeetingDeviceSettings({
  clientRef, localTracksRef, onMicChange, onCamChange, localVideoRef,
  meetingId, transcriptionOn, onToggleTranscription, recording, onToggleRecording,
  isHost = false, cloudRecordingConfigured = false,
}) {
  const [devices, setDevices] = useState({ mics: [], cams: [], speakers: [] });
  const [selected, setSelected] = useState({ micId: "", camId: "", speakerId: "" });
  const [switching, setSwitching] = useState(false);
  const [aiSummaryEnabled, setAiSummaryEnabled] = useState(true);
  const [savingAi, setSavingAi] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);

  const generateSummaryNow = async () => {
    if (!meetingId) return;
    if (!aiSummaryEnabled) {
      toast.error("AI summary is disabled for this meeting.");
      return;
    }
    setGeneratingSummary(true);
    try {
      await api.post(`/summaries/generate`, { meeting_id: meetingId });
      toast.success("AI summary generated — view it in the meeting's Follow-up tab.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate summary");
    } finally { setGeneratingSummary(false); }
  };

  // Load the current AI summary flag for this meeting so the toggle reflects
  // the persisted state (and survives panel re-mounts).
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!meetingId) return;
      try {
        const r = await api.get(`/recall/meeting/${meetingId}`);
        if (alive) setAiSummaryEnabled(r.data?.meeting?.ai_summary_enabled !== false);
      } catch {}
    })();
    return () => { alive = false; };
  }, [meetingId]);

  const toggleAiSummary = async () => {
    if (!meetingId) return;
    setSavingAi(true);
    try {
      const next = !aiSummaryEnabled;
      await api.patch(`/recall/meeting/${meetingId}/settings`, { ai_summary_enabled: next });
      setAiSummaryEnabled(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSavingAi(false); }
  };

  const refresh = useCallback(async () => {
    try {
      const [mics, cams] = await Promise.all([
        AgoraRTC.getMicrophones(),
        AgoraRTC.getCameras(),
      ]);
      let speakers = [];
      try {
        if (navigator.mediaDevices?.enumerateDevices) {
          const all = await navigator.mediaDevices.enumerateDevices();
          speakers = all.filter((d) => d.kind === "audiooutput");
        }
      } catch {}
      setDevices({ mics, cams, speakers });
      // Source of truth for which mic/cam is currently active = the saved
      // device preferences (which the lobby wrote and the room honoured when
      // creating its Agora tracks). Reading from MediaStreamTrack.getSettings
      // is unreliable because some browsers (and our mock stack) return generic
      // labels / synthetic deviceIds for canvas + AudioContext streams.
      const prefs = getDevicePrefs();
      setSelected({
        micId: prefs.micId || mics[0]?.deviceId || "",
        camId: prefs.camId || cams[0]?.deviceId || "",
        speakerId: speakers[0]?.deviceId || "",
      });
    } catch (e) {
      toast.error("Could not enumerate devices — check browser permissions.");
    }
  }, [localTracksRef]);
  useEffect(() => { refresh(); }, [refresh]);

  const changeMic = async (deviceId) => {
    setSwitching(true);
    try {
      const t = localTracksRef.current.audio;
      if (t && t.setDevice) {
        await t.setDevice(deviceId);
        setSelected((s) => ({ ...s, micId: deviceId }));
        setDevicePrefs({ micId: deviceId });
        toast.success("Microphone switched");
        onMicChange?.(t.enabled);
      }
    } catch (e) {
      toast.error("Could not switch microphone");
    } finally { setSwitching(false); }
  };
  const changeCam = async (deviceId) => {
    setSwitching(true);
    try {
      const t = localTracksRef.current.camera;
      if (t && t.setDevice) {
        await t.setDevice(deviceId);
        setSelected((s) => ({ ...s, camId: deviceId }));
        setDevicePrefs({ camId: deviceId });
        if (localVideoRef?.current) t.play(localVideoRef.current);
        toast.success("Camera switched");
        onCamChange?.(t.enabled);
      }
    } catch (e) {
      toast.error("Could not switch camera");
    } finally { setSwitching(false); }
  };
  const changeSpeaker = async (deviceId) => {
    setSelected((s) => ({ ...s, speakerId: deviceId }));
    // Browsers expose setSinkId on <audio>/<video> elements — Agora SDK doesn't have a unified hook.
    // We apply it to any audio elements created by the SDK so remote audio routes correctly.
    try {
      document.querySelectorAll("audio,video").forEach(async (el) => {
        if (typeof el.setSinkId === "function") {
          await el.setSinkId(deviceId);
        }
      });
      toast.success("Speaker switched");
    } catch {
      toast.error("Browser does not support speaker selection on this audio element");
    }
  };

  return (
    <div className="flex-1 flex flex-col p-3 overflow-y-auto">
      <p className="text-xs text-white/60 mb-3">Switch your microphone, camera, or speaker without leaving the meeting.</p>
      <DeviceSelect
        label="Microphone" icon={Mic} devices={devices.mics} value={selected.micId}
        onChange={changeMic} disabled={switching} testid="device-mic"
      />
      <DeviceSelect
        label="Camera" icon={Video} devices={devices.cams} value={selected.camId}
        onChange={changeCam} disabled={switching} testid="device-cam"
      />
      <DeviceSelect
        label="Speaker / output" icon={Settings} devices={devices.speakers} value={selected.speakerId}
        onChange={changeSpeaker} disabled={switching || devices.speakers.length === 0}
        emptyHint="Your browser doesn't expose speaker selection."
        testid="device-speaker"
      />

      <div className="mt-5 pt-4 border-t border-white/5">
        <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Recall · live controls</p>
        <p className="text-[11px] text-white/50 leading-snug mb-3">
          Turn capture features on or off for this meeting. Captures continue running
          even when you switch side panels.
        </p>
        <ControlRow
          label="Live transcription"
          desc="Browser captions for your local microphone only."
          checked={!!transcriptionOn}
          onToggle={onToggleTranscription}
          testid="settings-toggle-transcription"
        />
        <ControlRow
          label="Recording (host)"
          desc={
            cloudRecordingConfigured
              ? "Composite cloud recording — captures every participant's audio + video into a single 16:9 file."
              : "Cloud recording storage is not configured."
          }
          checked={!!recording}
          onToggle={onToggleRecording}
          disabled={!isHost || !cloudRecordingConfigured}
          testid="settings-toggle-recording"
        />
        <ControlRow
          label="AI summary after meeting"
          desc="Eligible for AI summary generation using the transcript, chat, and tasks."
          checked={!!aiSummaryEnabled}
          onToggle={toggleAiSummary}
          disabled={savingAi}
          testid="settings-toggle-ai-summary"
        />
        {/* Generate AI Summary — moved here from the (now-removed) Recall/AI
            nav panel so all AI controls live in one place. Disabled when
            AI summary is turned off for this meeting. */}
        <button
          onClick={generateSummaryNow}
          disabled={!aiSummaryEnabled || generatingSummary}
          className="w-full mt-3 px-3 py-2 rounded-lg text-sm font-medium bg-[#005e7a] hover:bg-[#005e7a]/90 text-white inline-flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="settings-generate-summary"
        >
          <Sparkles className="w-3.5 h-3.5" />
          {generatingSummary ? "Generating…" : "Generate AI summary"}
        </button>
        <p className="text-[11px] text-white/50 mt-2 leading-snug">
          Generates the summary now using the current transcript, notes, chat,
          and tasks. The summary appears in the meeting record's Follow-up tab
          and the downloadable Recall Report.
        </p>
      </div>
    </div>
  );
}

function ControlRow({ label, desc, checked, onToggle, disabled, testid }) {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className="w-full rounded-lg bg-white/5 p-3 text-left hover:bg-white/10 transition-colors disabled:opacity-50 mb-2"
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium">{label}</p>
        <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${
          checked ? "bg-[#16a34a]/30 text-[#bbf7d0]" : "bg-white/10 text-white/60"
        }`}>
          {checked ? "ON" : "OFF"}
        </span>
      </div>
      <p className="text-[11px] text-white/50 mt-1 leading-snug">{desc}</p>
    </button>
  );
}

function DeviceSelect({ label, icon: Icon, devices, value, onChange, disabled, emptyHint, testid }) {
  return (
    <div className="mb-4">
      <label className="text-[10px] uppercase tracking-widest text-white/50 font-semibold flex items-center gap-1 mb-1.5">
        <Icon className="w-3 h-3" /> {label}
      </label>
      {devices.length === 0 ? (
        <p className="text-xs text-white/40">{emptyHint || "No devices found."}</p>
      ) : (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-full bg-white/5 text-white text-xs px-2 py-2 rounded-md outline-none disabled:opacity-50"
          data-testid={testid}
        >
          {devices.map((d) => (
            <option key={d.deviceId} value={d.deviceId} className="bg-[#1a1a1f]">
              {d.label || `Device ${d.deviceId.slice(0, 6)}`}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

function LiveTranscriptPanel({
  meetingId, sttProvider, sttSession, sttEntries, workspaceDefaultLanguage,
  onStartFullMeeting, onStopFullMeeting, canControlFullMeeting,
}) {
  const scrollRef = useRef(null);
  // Selected language for the next start. Initializes from the workspace
  // default but is fully overridable per session. Disabled once live so
  // it's clear the language was committed when the agent joined.
  const [selectedLang, setSelectedLang] = useState(workspaceDefaultLanguage || "en-US");
  useEffect(() => {
    if (workspaceDefaultLanguage) setSelectedLang(workspaceDefaultLanguage);
  }, [workspaceDefaultLanguage]);

  // Full-meeting transcript entries — single source of truth. Browser
  // SpeechRecognition is INTENTIONALLY NOT wired into this panel; the
  // host's microphone is not a meeting transcript.
  const fullEntries = useMemo(() => sttEntries || [], [sttEntries]);
  const sttLive = !!(sttSession && sttSession.status === "live");
  const sttStatus = sttSession?.status || "disabled";
  const sttConfigured = !!sttProvider?.configured;

  const mountedAtCountRef = useRef(fullEntries.length);
  const [newWhileAway, setNewWhileAway] = useState(0);
  useEffect(() => {
    const captured = Math.max(0, fullEntries.length - mountedAtCountRef.current);
    setNewWhileAway(captured);
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [fullEntries]);

  // Status label — Agora session drives everything.
  let statusLabel = "Off";
  let statusColor = "bg-white/10 text-white/60";
  if (sttLive) { statusLabel = "Live"; statusColor = "bg-emerald-500/30 text-emerald-200"; }
  else if (sttStatus === "starting") { statusLabel = "Starting…"; statusColor = "bg-amber-500/30 text-amber-200"; }
  else if (sttStatus === "stopping") { statusLabel = "Stopping…"; statusColor = "bg-amber-500/30 text-amber-200"; }
  else if (sttStatus === "failed") { statusLabel = "Failed"; statusColor = "bg-red-500/30 text-red-200"; }
  else if (sttStatus === "completed") { statusLabel = "Completed"; statusColor = "bg-white/10 text-white/60"; }
  else if (!sttConfigured) { statusLabel = "Not configured"; statusColor = "bg-amber-500/30 text-amber-200"; }

  return (
    <div className="flex-1 flex flex-col p-4 overflow-hidden">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="bloq-headline text-base text-white">Full Meeting Transcription</h3>
        <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${statusColor}`} data-testid="transcript-status">
          {statusLabel}
        </span>
      </div>

      {newWhileAway > 0 && (
        <p className="text-[11px] text-emerald-300/90 bg-emerald-500/10 px-2 py-1.5 mb-3" data-testid="transcript-while-away">
          {newWhileAway} {newWhileAway === 1 ? "line" : "lines"} captured while you were on another panel.
        </p>
      )}

      <section className="mb-3 p-3 bg-white/5 border border-white/10" data-testid="transcript-full-meeting">
        {!sttConfigured ? (
          <p className="text-[12px] text-amber-200 leading-snug" data-testid="transcript-full-not-configured">
            Full meeting transcription is not configured yet. Ask a workspace admin to enable the Agora Real-Time Speech-to-Text integration to start transcribing every participant.
          </p>
        ) : !canControlFullMeeting ? (
          <p className="text-[12px] text-white/60 leading-snug" data-testid="transcript-full-not-permitted">
            Only the host or workspace members can start full meeting transcription. Lines will appear here once they do.
          </p>
        ) : (
          <>
            <p className="text-[11px] text-white/50 leading-snug mb-2">
              Captures meeting audio from the full call when configured. Speaker names map from the meeting roster where possible.
            </p>
            <label className="block mb-2">
              <span className="text-[10px] uppercase tracking-widest text-white/50">Language</span>
              <select
                value={selectedLang}
                onChange={(e) => setSelectedLang(e.target.value)}
                disabled={sttLive || sttStatus === "starting"}
                className="mt-1 w-full bg-white/5 text-white text-xs px-2 py-1.5 border border-white/10 outline-none disabled:opacity-50"
                data-testid="transcript-full-language"
              >
                {STT_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code} className="bg-[#1a1a1f]">{l.label}</option>
                ))}
              </select>
            </label>
            <button
              onClick={() => (sttLive ? onStopFullMeeting() : onStartFullMeeting(selectedLang))}
              disabled={sttStatus === "starting" || sttStatus === "stopping"}
              className={`w-full text-[12px] font-medium px-3 py-2 transition-colors disabled:opacity-50 ${sttLive ? "bg-red-600/90 hover:bg-red-600 text-white" : "bg-emerald-600 hover:bg-emerald-500 text-white"}`}
              data-testid="transcript-full-toggle"
            >
              {sttLive ? "Stop transcription" : sttStatus === "starting" ? "Starting…" : "Start transcription"}
            </button>
            {sttStatus === "failed" && (
              <p className="text-[11px] text-red-300 mt-2" data-testid="transcript-full-failed">
                Full meeting transcription failed to start. Please try again, or ask an admin to confirm the Agora STT integration is active.
              </p>
            )}
          </>
        )}
      </section>

      <div ref={scrollRef} className="flex-1 overflow-y-auto flex flex-col gap-1.5 pr-1 min-h-0" data-testid="transcript-entries">
        {fullEntries.length === 0 && (
          <p className="text-xs text-white/40 text-center mt-6">
            {sttLive
              ? "Listening to the full meeting… utterances will appear here."
              : sttConfigured
                ? "No transcript yet. Click Start to begin."
                : "Full meeting transcription is not configured yet."}
          </p>
        )}
        {fullEntries.map((e) => (
          <div key={e.entry_id} className="rounded-lg bg-emerald-500/5 border border-emerald-500/20 p-2">
            <p className="text-[10px] uppercase tracking-widest text-emerald-300/80">{e.speaker_name || "Speaker"}</p>
            <p className="text-sm text-white/90 mt-0.5">{e.text}</p>
          </div>
        ))}
      </div>
      {fullEntries.length > 0 && (
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/recall/meeting/${meetingId}/download?section=transcript&format=txt`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-[#005e7a] hover:underline mt-2 self-end"
        >
          Download transcript
        </a>
      )}
    </div>
  );
}

function MeetingEnded({ meetingId, publicId, elapsed }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Per Iter19 spec: no in-between "open recap / back to dashboard" screen.
  // - Logged-in users go straight to their dashboard.
  // - Guests land on the new account-CTA page (Bloq invitation).
  useEffect(() => {
    const t = setTimeout(() => {
      if (user?.user_id) {
        navigate("/app", { replace: true });
      } else {
        navigate(`/m/${publicId}/thanks`, { replace: true });
      }
    }, 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, []);
  // While the redirect resolves we show a tiny "Leaving the room…" placeholder
  // so the user never sees a blank screen. The placeholder has no buttons —
  // the redirect always wins.
  return (
    <div className="bloq-meeting-room flex items-center justify-center" data-testid="meeting-ended-redirect">
      <div className="bloq-card p-8 max-w-md text-[#1a1a1f] text-center">
        <BloqLogo size={24} />
        <p className="bloq-headline text-2xl mt-4">Leaving the room…</p>
        <p className="text-sm text-[#4a4a52] mt-2">Taking you to {user?.user_id ? "your dashboard" : "Bloq"}.</p>
      </div>
    </div>
  );
}


/**
 * WaitingRoomScreen — visually distinct from the Lobby:
 *
 *   Lobby   = pre-meeting preview screen (camera/mic check, name input)
 *   Waiting = post-lobby holding area while waiting for the host to admit
 *   Meeting = the live Agora room
 *
 * This page intentionally has no device preview / no Join button — once the
 * user has clicked Join from the Lobby their request is in the queue and
 * they must wait for the host. They can Leave at any time.
 */
function WaitingRoomScreen({ meetingTitle, hostName, displayName, onLeave }) {
  return (
    <div
      className="bloq-meeting-room flex items-center justify-center"
      data-testid="waiting-room-screen"
    >
      <div className="bloq-card p-10 max-w-lg text-center text-[#1a1a1f]" style={{ background: "rgba(255,255,255,0.97)" }}>
        <BloqLogo size={26} />
        <div className="mt-6 mb-5 mx-auto w-20 h-20 rounded-full bg-[#005e7a]/10 flex items-center justify-center">
          <div className="w-10 h-10 rounded-full bg-[#005e7a]/20 animate-pulse flex items-center justify-center">
            <Users className="w-5 h-5 text-[#005e7a]" />
          </div>
        </div>
        <p className="bloq-eyebrow text-[#005e7a]">Waiting room</p>
        <h1 className="bloq-headline text-3xl mt-2 mb-1" data-testid="waiting-room-title">
          {meetingTitle || "Meeting"}
        </h1>
        <p className="text-sm text-[#4a4a52] mt-3" data-testid="waiting-room-message">
          You're in the waiting room. {hostName ? <>The host <strong>{hostName}</strong> will admit you shortly.</> : "The host will admit you shortly."}
        </p>
        {displayName && (
          <p className="text-xs text-[#82828c] mt-4">
            You'll join as <strong className="text-[#1a1a1f]">{displayName}</strong>.
          </p>
        )}
        <button
          onClick={onLeave}
          className="bloq-btn-secondary mt-7 inline-flex items-center gap-2"
          data-testid="waiting-room-leave"
        >
          <PhoneOff className="w-3.5 h-3.5" /> Leave waiting room
        </button>
      </div>
    </div>
  );
}
